<template>
  <div class="complex-table-component">
    <div
      v-if="readOnly"
      class="alert alert-info mb-2"
      role="alert"
    >
      <p class="mb-0">
        Displayed data is as of {{ displayDate }}
      </p>
    </div>

    <b-card
      no-body
      class="complex-table"
      :class="{'h-93': readOnly}"
    >
      <b-collapse
        id="applied-filters-collapse"
        :visible="showFilters"
      >
        <div class="filters-content">
          <div class="filters-wrapper">
            <filter-header
              :filters="filters"
              @filter="onFilter"
            />
            <sorting-header
              :sorting="sorting"
              :grouping="[groupBy, subGroupBy]"
              @sortChange="onSortChange"
            />
            <grouping-header
              :group-by="groupBy"
              :sub-group-by="subGroupBy"
              @groupingChange="onGroupByChange"
            />
            <div class="d-flex">
              <label
                for="switch"
                class="mr-2"
              > Limited row height:</label>
              <b-form-checkbox
                v-model="styles.limitRowHeight"
                switch
                @change="setFixedRowHeightState"
              />
            </div>
          </div>
          <entity-views-crud
            :entity-uuid="$route.params.tableId"
            :views="table.views"
            :filters="getFilters()"
            :params="pagedDataParams"
            :styles="styles"
            :sticky-columns="stickyColumns"
            @chooseView="chooseView"
          />
        </div>
      </b-collapse>
      <content-spinner :show="tableLoading || loading" />
      <no-content
        :show="!isRowsAfterLoadingExist && !tableLoading && !loading"
        text="No Data Available"
      />
      <no-content
        :show="!totalRows && !!isRowsAfterLoadingExist && !tableLoading && !loading"
        class="no-filter-results"
        text="There are no records matching your request"
      />
      <div class="table-wrapper">
        <b-table
          v-if="isRowsAfterLoadingExist && !tableLoading && !loading"
          ref="table"
          striped
          class="full-min-height"
          :sticky-header="true"
          :head-variant="'dark'"
          :small="small"
          :hover="hover"
          :fields="getFieldsForTable"
          :items="getItems"
          :sort-by="sortBy"
          :sort-desc="sortDesc"
          :no-local-sorting="true"
          :style="getTableWidth"
          :tbody-tr-class="rowClass"
          :tbody-tr-attr="rowAttributes"
          @sort-changed="onSortChanged"
          @row-contextmenu="onRowClick"
        >
          <template #head()="data">
            <b-form-checkbox
              v-if="isSelectColumn(data.field)"
              id="selectAllColumn"
              title="Select All"
              :checked="table.checkedAll"
              @change="checkAll($event)"
            />
            <template v-if="data.field.displayInTable">
              <template>
                {{ displayLabel(data) }}
                <filter-dropdown
                  v-if="showFilterDropdown(data)"
                  :data="data"
                  :options="[]"
                  :selected="selected"
                  :filters="filters"
                  :group-by="groupBy"
                  :sub-group-by="subGroupBy"
                  :is-grouped="groupBy === data.field.key || subGroupBy === data.field.key"
                  :is-sticky="stickyColumns.includes(data.field.name)"
                  :is-loan-calculator-output="isLoanCalculatorOutput"
                  @filter="onFilter"
                  @sort-change="onSortChanged"
                  @group-by-change="onGroupByChange"
                  @column-freeze="onColumnFreeze"
                  @hide-context-menu="onOutsideClick"
                />
              </template>
              <span
                v-if="!isStaticColumn(data.field)"
                class="header-resize-action"
                @mousedown="onColumnResize($event, data.field)"
              />
            </template>
          </template>
          <template #cell()="{item, index, field}">
            <div
              :class="`cell-${getValidationType(item.failedFields, field.fieldGuid)}`"
              :title="getWarning(item.failedFields, field.fieldGuid)"
              data-toggle="tooltip"
            >
              <template v-if="field.displayInTable">
                <div
                  v-if="item.isGroup || item.isSubgroup"
                  class="py-2 px-3 group-handler"
                  :class="item.isSubgroup ? 'pl-5' : ''"
                  @click="toggleGroupRows(item)"
                >
                &nbsp;
                </div>
                <div
                  v-else
                  class="td-content-wrapper"
                  :class="{
                    'td-editing': isInEditing(index, field),
                    'position-relative': loadingCell.column === field.name && loadingCell.row === index
                  }"
                >
                  <span
                    v-if="loadingCell.column === field.name && loadingCell.row === index"
                    class="cell-loading"
                  >
                    <b-spinner small />
                  </span>
                  <div
                    v-if="editingCell.index === index"
                    class="input-editor-wrapper"
                    :class="'editor-wrapper-'+field.dataTypeName"
                  >
                    <div
                      v-if="isRLSField(field) && isInEditing(index, field)"
                      style="display: flex;"
                      @blur="finishEditing($event, index, item, field)"
                    >
                      <user-list-popover
                        :key="isInEditing(index, field)"
                        ref="user-popover"
                        v-model="vModelData[index][field.name]"
                        :selected-users="JSON.parse(item[field.name] || '[]')"
                        :is-single-page="false"
                        :component-id="`row-${index}`"
                        :finish-editing="($event) => { finishEditing($event, index, item, field);}"
                        :render-button="false"
                        @userSelect="(user) => rlsUserSelect(user, index, item, field)"
                        @userRemove="(user) => rlsUserRemove(user, index, item, field)"
                      />
                      <span class="rls" :title="getRLSUsersAndGroupsForDisplay(item, field)">{{ getRLSUsersAndGroupsForDisplay(item, field) }}</span>
                    </div>
                    <lookup-input-component
                      v-show="isMultiSelect(field) && isMultiselectActivated && isInEditing(index, field)"
                      :key="isInEditing(index, field)"
                      transit-ref="lkp-multiselect"
                      :is-validation-rules="false"
                      :is-edit-modal="false"
                      :props-value="vModelData[index][field.name]"
                      :field="field"
                      :item="item"
                      :index="index"
                      :hide-error-msg="true"
                      @on-close="onCloseEvent"
                      @on-select="finishLkpEditing"
                      @on-add-new-relation="onAddNewRelationalEntity"
                    />
                    <treeselect
                      v-if="isHierarchyTree(field)"
                      v-model="vModelData[index][field.name]"
                      :normalizer="field.config"
                      :options="field.tree"
                      :close-on-select="closeTreeSelect"
                      @close="onCloseEvent"
                      @select="onSelectValue($event, index, item, field)"
                      @input="onClearData($event, index, item, field)"
                    >
                      <label
                        slot="option-label"
                        slot-scope="{ node, shouldShowCount, count, labelClassName, countClassName }"
                        :class="{'disabled': node.raw.disabled, [labelClassName]: labelClassName}"
                      >
                        {{ node.label }}
                      </label>
                    </treeselect>
                    <b-form-input
                      v-if="isFieldEmail(field)"
                      :ref="`currentInput_${index}_${field.ordinalPosition}`"
                      v-model="vModelData[index][field.name]"
                      type="email"
                      class="input-editor"
                      @blur="finishEditing($event, index, item, field)"
                      @keydown.esc="cancelEditing($event, index, item, field)"
                      @keydown.enter="onEnterKey($event, index, item, field)"
                    />
                    <search-input
                      v-if="isSearchInput(field) && !isPostedStatus(field)"
                      v-model="vModelData[index][field.name]"
                      :plugin-config="{class: 'input-editor'}"
                      :focus="true"
                      :state="null"
                      :suggestions="field.suggestions ? field.suggestions[field.name]: []"
                      @blur="finishEditing($event, index, item, field)"
                      @keydown.esc="cancelEditing($event, index, item, field)"
                      @keydown.enter="onEnterKey($event, index, item, field)"
                    />
                    <div v-else-if="isFieldText(field) && !isPostedStatus(field) && !isRLSField(field)">
                      <b-form-textarea
                        v-if="isLineBreakTextField(field)"
                        :ref="`currentInput_${index}_${field.ordinalPosition}`"
                        v-model="vModelData[index][field.name]"
                        class="input-editor"
                        rows="1"
                        :style="`height: ${lineBreakTextAreaHeight}px`"
                        :disabled="field.disabled"
                        @blur="finishEditing($event, index, item, field)"
                        @keydown.esc="cancelEditing($event, index, item, field)"
                        @keydown="onEnterKey($event, index, item, field)"
                      />
                      <b-form-input
                        v-else
                        :id="`${field.name}-${index + 1}`"
                        :ref="`currentInput_${index}_${field.ordinalPosition}`"
                        v-model="vModelData[index][field.name]"
                        :disabled="field.disabled"
                        type="text"
                        class="input-editor"
                        @blur="finishEditing($event, index, item, field)"
                        @keydown.esc="cancelEditing($event, index, item, field)"
                        @keydown.enter="onEnterKey($event, index, item, field)"
                      />
                    </div>
                    <b-form-input
                      v-if="isFieldNumber(field)"
                      :id="`${field.name}-${index + 1}`"
                      :ref="`currentInput_${index}_${field.ordinalPosition}`"
                      v-model="vModelData[index][field.name]"
                      type="number"
                      class="input-editor"
                      @blur="finishEditing($event, index, item, field)"
                      @keydown.esc="cancelEditing($event, index, item, field)"
                      @keydown.enter="onEnterKey($event, index, item, field)"
                    />
                    <custom-date-picker
                      v-if="isFieldDatetime(field)"
                      :id="`currentInput_${index}_${field.ordinalPosition}`"
                      :ref="`currentInput_${index}_${field.ordinalPosition}`"
                      v-model="vModelData[index][field.name]"
                      type="datetime"
                      input-class="input-editor form-control"
                      :disabled="field.disabled"
                      class="editor-w"
                      :format="getDateFormat(field)"
                      :placeholder="getDateFormat(field)"
                      :value-type="getDateFormat(field)"
                      :open="customDateChange[`open_${index}_${field.ordinalPosition}`]"
                      @input="onDatePickerInputChange"
                      @clear="onClearData($event, index, item, field)"
                      @close="finishEditing($event, index, item, field)"
                      @open="onDatePickerOpen($event, index, item, field)"
                    />
                    <custom-date-picker
                      v-if="isFieldDate(field)"
                      :id="`currentInput_${index}_${field.ordinalPosition}`"
                      :ref="`currentInput_${index}_${field.ordinalPosition}`"
                      v-model="vModelData[index][field.name]"
                      input-class="input-editor form-control"
                      class="editor-w"
                      :disabled="field.disabled"
                      :format="getDateFormat(field)"
                      :value-type="getDateFormat(field)"
                      :placeholder="getDateFormat(field)"
                      :open="customDateChange[`open_${index}_${field.ordinalPosition}`]"
                      @pick="finishEditing($event, index, item, field)"
                      @close="finishEditing($event, index, item, field)"
                      @clear="onClearData($event, index, item, field)"
                      @open="onDatePickerOpen($event, index, item, field)"
                    />
                    <multiselect
                      v-if="isPostedStatus(field)"
                      :id="`select-${field.name}-${index}`"
                      :ref="`select-${field.name}-${index}`"
                      v-model="vModelData[index][field.name]"
                      :options="field.data.options"
                      :track-by="field.config.optionValue"
                      :label="field.config.optionLabel"
                      :multiple="field.config.multiple"
                      :close-on-select="field.config.closeOnSelect"
                      :show-labels="false"
                      @select="finishEditing($event,index,item,field)"
                      @close="onCloseEvent"
                    />
                    <b-dropdown
                      v-if="isFieldSelect(field)"
                      :ref="'inputEditor_'+index"
                      variant="light"
                      no-caret
                      :text="getSelectText(field,item)"
                      class="input-editor"
                    >
                      <b-dropdown-item
                        v-for="option in field.data.options"
                        @click="selectChange(option, index, item, field)"
                      >
                        {{ option.text }}
                      </b-dropdown-item>
                    </b-dropdown>
                    <div
                      v-if="inlineEditButtons"
                      class="button-toolbar"
                    >
                      <b-button
                        variant="success"
                        class="mr-2"
                      >
                        Save
                      </b-button>
                      <b-button variant="danger">
                        Cancel
                      </b-button>
                    </div>
                  </div>
                  <div
                    class="td-content"
                    tabindex="0"
                    :style="field.tdStyle"
                    :column="field.name"
                    :class="{'border border-primary': isFirstClick(index, field)}"
                    data-placement="bottom"
                    @click="onClick($event, index, item, field)"
                    @keydown="nextRow($event, index, item, field)"
                    @keydown.esc="clearCellEdit"
                    @keydown.enter="onEnterKey($event, index, item, field)"
                  >
                    <span
                      v-if="isFieldEmail(field)"
                      :id="`${field.name}-${index + 1}`"
                      :class="{'disable-text': hasPostedValue(item)}"
                      class="td-actual-content"
                    >
                      {{ item[field.name] }}
                    </span>
                    <span
                      v-if="isFieldText(field) && !isPostedStatus(field) && !isRLSField(field)"
                      :id="`${field.name}-${index + 1}`"
                      :ref="`content_${index}_${field.ordinalPosition}`"
                      class="text-break td-actual-content"
                      :class="{'disable-text': hasPostedValue(item)}"
                      v-html="getValueForTextField(item, field)"
                    />
                    <span
                      v-if="isFieldNumber(field)"
                      :id="`${field.name}-${index + 1}`"
                      data-toggle="tooltip"
                      data-placement="bottom"
                      :title="getNumberValue(item[field.name], field)"
                      :class="{'disable-text': hasPostedValue(item)}"
                      class="td-actual-content"
                    >
                      {{ getNumberValue(item[field.name], field) }}
                    </span>
                    <span
                      v-if="isFieldDate(field)"
                      :id="`${field.name}-${index + 1}`"
                      data-toggle="tooltip"
                      data-placement="bottom"
                      :title="item[field.name] | toDate(JSON.parse(field.dataTypeJSON).FormatString)"
                      :class="{'disable-text': hasPostedValue(item)}"
                      class="td-actual-content"
                    >
                      {{ item[field.name] | toDate(JSON.parse(field.dataTypeJSON).FormatString) }}
                    </span>
                    <span
                      v-if="isFieldDatetime(field)"
                      :id="`${field.name}-${index + 1}`"
                      data-toggle="tooltip"
                      data-placement="bottom"
                      :title="item[field.name] | toDatetime(dateTimeFormat(field))"
                      :class="{'disable-text': hasPostedValue(item)}"
                      class="td-actual-content"
                    >
                      {{ item[field.name] | toDatetime(dateTimeFormat(field)) }}
                    </span>
                    <span
                      v-if="isFieldCheckbox(field)"
                      class="td-actual-content"
                    >
                      <b-form-checkbox
                        :id="`${field.name}-${index + 1}`"
                        v-model="item[field.name]"
                        :disabled="!canEditField(field) || hasPostedValue(item)"
                        :checked="item[field.name]"
                        @change="onCheckboxChange($event, index, item, field)"
                      />
                    </span>
                    <span
                      v-if="isPostedStatus(field)"
                      :id="`${field.name}-${index + 1}`"
                      data-toggle="tooltip"
                      data-placement="bottom"
                      :title="getSelectText(field, item)"
                      class="td-actual-content"
                    >
                      {{ getSelectText(field, item) }}
                    </span>
                    <span
                      v-if="isFieldHierarchy(field)"
                      :id="`${field.name}-${index + 1}`"
                      data-toggle="tooltip"
                      data-placement="bottom"
                      :title="getHierarchyText(field, item)"
                      :class="{'disable-text': hasPostedValue(item)}"
                      class="td-actual-content"
                    >
                      {{ getHierarchyText(field, item) }}
                    </span>
                    <span
                      v-if="isMultiSelect(field)"
                      :id="`${field.name}-${index + 1}`"
                      data-toggle="tooltip"
                      data-placement="bottom"
                      :title="getMultiSelectValue(item, field)"
                      :class="{
                        'not-exist': item.customClass && isValidSuggestions(item[`${field.name}__$LookupStatus`]),
                        'disable-text': hasPostedValue(item)
                      }"
                      class="td-actual-content"
                    >
                      {{ getMultiSelectValue(item, field) }}
                    </span>
                    <span
                      v-if="isRLSField(field)"
                      :id="`${field.name}-${index + 1}`"
                      :title="getRLSUsersAndGroupsForDisplay(item, field)"
                      class="action-button"
                    >
                      {{ getRLSUsersAndGroupsForDisplay(item, field) }}
                    </span>
                    <template v-if="isFieldDocument(field)">
                      <span
                        v-for="(doc, docIndex) in getDocumentDataFromField(item, field)"
                        :title="getDocumentTooltip(item, field)"
                        class="td-actual-content text-center mr-2 cursor-pointer"
                        @click="openDocumentPreview(item, field)"
                      >
                        <font-awesome-icon
                          :icon="getFileTypeIconForTemplate(doc.name)"
                          class="mr-1"
                          :style="getFileTypeColourForTemplate(doc.name)"
                        />
                        {{ doc.name }}<span v-if="docIndex !== getDocumentDataFromField(item, field).length - 1">,</span>
                      </span>
                    </template>
                  </div>
                </div>
              </template>
            </div>
          </template>
          <template
            v-if="canEdit || canDelete || canRead"
            #cell(actions)="{item, index, field}"
          >
            <div
              v-if="item.isGroup || item.isSubgroup"
              class="py-2 px-3 group-handler"
              :class="item.isSubgroup ? 'pl-5' : ''"
              @click="toggleGroupRows(item)"
            >
              &nbsp;
            </div>
            <div
              v-else
              class="td-content-wrapper"
            >
              <div class="td-content px-2 d-flex justify-content-center align-items-center">
                <font-awesome-icon
                  v-if="canCheck && isValidField(item)"
                  title="Confirm"
                  class="text-success action-button mr-2"
                  :class="{'disabled-button': readOnly}"
                  :icon="['fas', 'check']"
                  @click="onConfirmClick(item, index, field)"
                />
                <font-awesome-icon
                  v-else-if="isValidatedTable"
                  :icon="['fas', 'check']"
                  class="mr-2"
                  style="opacity: 0"
                />
                <font-awesome-icon
                  v-if="canEdit"
                  :id="`${field.key}-${index + 1}-edit`"
                  title="Edit"
                  class="text-primary action-button mr-2"
                  :class="{'disabled-button': readOnly}"
                  :icon="['fas', 'edit']"
                  @click="onEditClick(item, index, field)"
                />
                <font-awesome-icon
                  v-if="canDelete"
                  :id="`${field.key}-${index + 1}-delete`"
                  title="Delete"
                  class="text-danger action-button mr-2"
                  :class="{'disabled-button': readOnly || hasPostedValue(item)}"
                  :icon="['fas', 'trash']"
                  @click="onDeleteClick(item, index, field)"
                />
                <font-awesome-icon
                  :id="`${field.key}-${index + 1}-history`"
                  class="text-warning action-button"
                  title="History"
                  :icon="['fas', 'history']"
                  @click="onHistoryClick(item, index, field)"
                />
              </div>
            </div>
          </template>
          <template #cell(recordStatus)="{item, index, field}">
            <div
              v-if="item.isGroup || item.isSubgroup"
              class="py-2 px-3 group-handler"
              :class="item.isSubgroup ? 'pl-5' : ''"
              @click="toggleGroupRows(item)"
            >
              &nbsp;
            </div>
            <div
              v-else
              class="td-content-wrapper"
            >
              <div
                class="td-content text-center px-2 pr-4"
                column="RecordStatus"
              >
                <b-badge
                  :id="`recordStatus-${index + 1}`"
                  data-toggle="tooltip"
                  data-placement="bottom"
                  :title="getStatusValue(item)"
                  :class="{'disable-text': hasPostedValue(item)}"
                  :variant="getStatusBadgeVariant(item)"
                  class="td-actual-content"
                >
                  {{ getStatusValue(item) }}
                </b-badge>
              </div>
            </div>
          </template>
          <template #cell(selectColumn)="{item, index}">
            <div
              v-if="item.isGroup"
              class="py-2 px-3 group-handler"
              :class="item.isSubgroup ? 'pl-5' : ''"
              @click="toggleGroupRows(item)"
            >
              <strong>
                <font-awesome-icon
                  :icon="['fas', 'layer-group']"
                  class="ml-2"
                />
                <span class="group-name-text">
                  <template v-if="isFieldEmail(groupByField) || isFieldText(groupByField)">
                    {{ item[groupByField.name] }}
                  </template>
                  <template v-if="isFieldNumber(groupByField)">
                    {{ getNumberValue(item[groupByField.name], groupByField) }}
                  </template>
                  <template v-if="isFieldDate(groupByField)">
                    {{ item[groupByField.name] | toDate(JSON.parse(groupByField.dataTypeJSON).FormatString) }}
                  </template>
                  <template v-if="isFieldDatetime(groupByField)">
                    {{ item[groupByField.name] | toDatetime(dateTimeFormat(groupByField)) }}
                  </template>
                  <template v-if="isFieldCheckbox(groupByField)">
                    {{ item[groupByField.name] ? 'Yes' : 'No' }}
                  </template>
                  <template v-if="isFieldHierarchy(groupByField)">
                    {{ getHierarchyText(groupByField, item) }}
                  </template>
                  <template v-if="isMultiSelect(groupByField)">
                    {{ getMultiSelectValue(item, groupByField) }}
                  </template>
                  <template v-if="isFieldRecordStatus(groupByField)">
                    <span v-if="validation.validationFailed === item.recordStatus">
                      Validation Failed
                    </span>
                    <span v-else>
                      {{ capitalize(item.recordStatus) }}
                    </span>
                  </template>
                </span>
              </strong>
              -
              <button
                class="grouping-remove"
                @click="removeGrouping(item)"
              >
                Remove Grouping
              </button>
            </div>
            <div
              v-else-if="item.isSubgroup"
              class="py-2 px-3 group-handler"
              :class="item.isSubgroup ? 'pl-5' : ''"
              @click="toggleGroupRows(item)"
            >
              <strong>
                <font-awesome-icon
                  :icon="['fas', 'layer-group']"
                  class="ml-2"
                />
                <span class="group-name-text">
                  <template v-if="isFieldEmail(subGroupByField) || isFieldText(subGroupByField)">
                    {{ item[subGroupByField.name] }}
                  </template>
                  <template v-if="isFieldNumber(subGroupByField)">
                    {{ getNumberValue(item[subGroupByField.name], subGroupByField) }}
                  </template>
                  <template v-if="isFieldDate(subGroupByField)">
                    {{ item[subGroupByField.name] | toDate(JSON.parse(subGroupByField.dataTypeJSON).FormatString) }}
                  </template>
                  <template v-if="isFieldDatetime(subGroupByField)">
                    {{ item[subGroupByField.name] | toDatetime(dateTimeFormat(subGroupByField)) }}
                  </template>
                  <template v-if="isFieldCheckbox(subGroupByField)">
                    {{ item[subGroupByField.name] ? 'Yes' : 'No' }}
                  </template>
                  <template v-if="isFieldHierarchy(subGroupByField)">
                    {{ getHierarchyText(subGroupByField, item) }}
                  </template>
                  <template v-if="isMultiSelect(subGroupByField)">
                    {{ getMultiSelectValue(item, subGroupByField) }}
                  </template>
                </span>
              </strong>
              -
              <button
                class="grouping-remove"
                @click="removeGrouping(item)"
              >
                Remove Grouping
              </button>
            </div>
            <div
              v-else
              class="td-content-wrapper"
            >
              <div class="d-flex td-content">
                <b-form-checkbox
                  :id="`selectColumn-${index + 1}`"
                
                  :checked="table.checkedItems.includes(item)"
                  :disabled="hasPostedValue(item)"
                  @change="bulkUpdateCheck($event, item)"
                />
                <!-- Display at start of row if there is a warning validation failure in row -->
                <font-awesome-icon
                  v-if="(item.failedFields.length > 0 && item.failedFields.some(f => f.type === 'warning'))"
                  :icon="['fas', 'circle-xmark']"
                  size="lg"
                  class="text-danger mt-1 ml-2"
                  data-toggle="tooltip"
                  title="Validation Failed"
                />
                <!-- Display at start of row if there is a data quality validation failure in row -->
                <font-awesome-icon
                  v-if="(item.failedFields.length > 0 && item.failedFields.some(f => f.type === 'dataQuality'))"
                  :icon="['fas', 'circle-info']"
                  size="lg"
                  class="text-warning mt-1 ml-2"
                  data-toggle="tooltip"
                  title="Data Quality Issue"
                />
              </div>
            </div>
          </template>
        </b-table>
      </div>
      <div class="d-flex p-3 justify-content-between main-header-wrapper">
        <div class="d-flex align-items-center">
          <pagination-summary
            v-if="totalRows > 0"
            :total="totalRows"
            :page="currentPage"
            :per-page="perPage"
          />
          <b-form-select
            v-model="perPage"
            class="per-page-select ml-2"
            :options="perPageOptions"
            @change="onPerPageChange"
          />
        </div>
        <b-pagination
          v-if="totalRows > perPage"
          v-model="currentPage"
          class="mb-0"
          :total-rows="totalRows"
          :per-page="perPage"
          @input="onCurrentPageChange"
        />
      </div>
    </b-card>
    <ImportModal
      :type="importType"
      :fields="importFields"
    />
    <ImportTableViewModal @changed-row="onChangedRow" />
    <view-row-modal />
    <edit-sections-modal
      v-if="modalState.isModalOpened"
      @changed-row="onChangedRow"
    />
    <edit-row-modal
      v-for="(value, modalName, index) in relationalModals"
      :modal-key="modalName"
      @changed-row="onChangedRow"
      @add-new-entity="addNewEntity"
      @is-bulk-update-happened="isBulkUpdateHappened"
    />
    <ComplexTableContextMenu
      v-if="isContextMenuOpen"
      :on-history="onHistoryClick"
      :on-confirm="onConfirmClick"
      :on-delete="onDeleteClick"
      :on-edit="onEditClick"
      :is-check-active="canCheck && isValidField(currentItem)"
      :is-deletable="canDelete"
      :is-editable="canEdit"
      :table-item="currentItem"
      :idx="currentIdx"
      :left="left"
      :top="top"
      @on-outside-click="onOutsideClick"
    />
    <SharePointPreviewerModal
      v-if="showDocumentPreviewModal"
      :documents="documentPreviewData"
      @hidden="onDocumentPreviewModalHidden"
      @changed-row="onChangedRow"
    />
  </div>
</template>

<script>
import moment from 'moment';
import pixelWidth from 'string-pixel-width';
import _, {capitalize, debounce} from 'lodash';
import {createNamespacedHelpers} from 'vuex';
import {axiosClient} from '@/core/services/http.service';
import getFormattedNumber from '../../filters/number.filter';
import fieldMixin from '../../mixins/field.mixin';
import {getFailedValidationFields} from '@/core/utils/getFailedValidationFields';
import {getFileTypeIcon, getFileTypeColour} from '@/core/utils/fileTypeUtils';
// Components
import PaginationSummary from './components/PaginationSummary';
import NoContent from '../NoContent';
import InputWidget from './components/InputWidget';
import ViewRowModal from './ViewRowModal';
import FilterHeader from './components/FilterHeader';
import FilterDropdown from './components/FilterDropdown';
import ImportModal from './ImportModal';
import ImportTableViewModal from './ImportTableViewModal';
import ContentSpinner from '../ContentSpinner';
import SortingHeader from './components/SortingHeader.vue';
import GroupingHeader from './components/GroupingHeader.vue';
import EntityViewsCrud from '../../../modules/DataManagement/views/table/EntityViewsCrud.vue';
import ComplexTableContextMenu from '@/core/components/complex-table/components/ComplexTableContextMenu.vue';
import EditSectionsModal from '@/core/components/complex-table/EditSectionsModal.vue';
import EditRowModal from '@/core/components/complex-table/EditRowModal.vue';
import LookupInputComponent from '@/core/components/LookupInputComponent.vue';
import UserListPopover from '@/modules/DataManagement/components/UserListPopover';
import SharePointPreviewerModal from '@/core/components/complex-table/SharePointPreviewerModal.vue';

const {mapState, mapActions, mapGetters} = createNamespacedHelpers('dataManagement');
const {mapState: sidebarMapState} = createNamespacedHelpers('sidebar');
const {mapActions: mapActionsUser, mapState: mapStateUsers} = createNamespacedHelpers('user');
const {
  mapState: designerMapState,
  mapActions: designerMapActions,
} = createNamespacedHelpers('designer');
export default {
  name: 'ComplexTable',
  components: {
    LookupInputComponent,
    EditSectionsModal,
    ComplexTableContextMenu,
    EntityViewsCrud,
    GroupingHeader,
    SortingHeader,
    UserListPopover,
    ContentSpinner,
    ImportTableViewModal,
    ImportModal,
    NoContent,
    PaginationSummary,
    InputWidget,
    ViewRowModal,
    FilterHeader,
    FilterDropdown,
    EditRowModal,
    SharePointPreviewerModal,
  },
  mixins: [fieldMixin],
  props: {
    items: {
      type: Array,
      required: true,
    },
    pagedDataParams: Object,
    small: {
      type: Boolean,
      default: false,
    },
    hover: {
      type: Boolean,
      default: false,
    },
    fields: Array,
    size: {
      type: String,
      default: '',
    },
    inlineEditButtons: {
      type: Boolean,
      default: false,
    },
    totalRows: {
      type: Number,
      required: true,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    sortable: {
      type: Boolean,
      default: true,
    },
    isLoanCalculatorOutput: {
      type: Boolean,
      default: false,
    },
    allField: Array,
    loading: Boolean,
    loadingCell: {
      type: Object,
      default: () => ({
        row: -1,
        column: null,
      }),
    },
    showFilters: Boolean,
    groupBy: String,
    subGroupBy: String,
    sorting: Array,
  },
  data() {
    return {
      allRLSUsers: [],
      isMultiselectActivated: false,
      styles: {
        limitRowHeight: false,
      },
      fieldsForTable: null,
      allRows: null,
      observer: null,
      intl: null,
      top: 0,
      left: 0,
      side: 'left',
      currentItem: {},
      currentIdx: 0,
      lineBreakTextAreaHeight: 50,
      tableLoading: false,
      displayDate: moment().format('YYYY-MM-DD'),
      sortBy: null,
      sortDesc: false,
      stickyColumns: ['selectColumn'],
      currentPage: 1,
      perPage: 50,
      perPageOptions: [20, 50, 100, 200, 500],
      localFields: [],
      localItems: [],
      columnWidths: [],
      selected: 'Select custom filter',
      filters: {},
      editingCell: {
        rowIndex: null,
        columnKey: null,
        index: null,
        item: null,
        field: null,
        click: 0,
      },
      vModelData: [],
      closeTreeSelect: false,
      customDateChange: {
        inputDate: '',
        inputEdit: false,
      },
      importType: '',
      importFields: [],
      recordStatusField: {
        key: 'recordStatus',
        sortable: true,
        label: 'Record Status',
        tdClass: 'align-top',
        thStyle: {width: '150px'},
        thClass: 'text-center pr-4',
        thAttr: {column: 'recordStatus'},
      },
      saveSearchMapForDebounce: new Map(),
      backupFields: [],
      isRowsAfterLoadingExist: null,
      showDocumentPreviewModal: false,
      documentPreviewData: [],
      itemInDocumentPreview: null,
      fieldInDocumentPreview: null,
    };
  },
  watch: {
    async backupFields(val) {
      for (const i of val) {
        if (i.dataTypeName === 'lookup' && i.relationalFieldId) {
          const elementValues = this.lookupValues[i.fieldGuid];
          if (!elementValues) return;
          const firstOptions = [...elementValues.map(item => ({
            id: item.recordGuid || item.id,
            text: item.value || item.text,
          }))];
          i.data.options.push(...firstOptions);
        }
      }
    },
    fields() {
      this.localFields = JSON.parse(JSON.stringify(this.fields));
      this.backupFields = JSON.parse(JSON.stringify(this.fields));
      this.mergedFields();
      this.$nextTick(() => {
        if (this.$refs.table) {
          this.observer = new IntersectionObserver(this.hideShow, {
            root: this.$refs.table.$el,
            threshold: 0.1,
          });
          this.allRows = this.$refs.table.$el.querySelectorAll('th');
          this.allRows.forEach(th => this.observer.observe(th));
        }
      });
    },
    items() {
      this.localItems = this.items;

      // Update documentPreviewData if the Preview modal is open
      if (this.showDocumentPreviewModal) this.updateDocumentPreviewData(this.itemInDocumentPreview, this.fieldInDocumentPreview);

      this.$nextTick(() => {
        if (this.$refs.table) {
          this.$refs.table.$el.addEventListener('scroll', this.onScrollChange);
        }
      });
    },
    displayDate() {
      this.$emit('display-date-change', this.displayDate);
    },
    selectedView: {
      handler(newView) {
        if (newView && newView.viewGuid) {
          const viewJSON = JSON.parse(newView.viewJSON || '{}');
          this.filters = viewJSON.frontFilters;
          if (viewJSON.styles) {
            this.styles = viewJSON.styles;
          }
          if (viewJSON.stickyColumns) {
            viewJSON.stickyColumns.forEach(col => {
              if (col && !this.stickyColumns.includes(col)) {
                this.stickyColumns.push(col);
              }
            });
          }

          if (viewJSON && viewJSON.pageSize) {
            this.perPage = viewJSON.pageSize;
          }

          if (viewJSON && viewJSON.toggleColumns) {
            this.saveToggleColumns({columns: viewJSON.toggleColumns});
          } else {
            this.saveToggleColumns({columns: false});
          }
        }
      },
      deep: true,
      immediate: true,
    },
    users: {
      handler(newVal) {
        newVal.data.forEach(newUser => {
          if (!this.allRLSUsers.some(u => u.userGuid === newUser.id)) {
            this.allRLSUsers.push({userGuid: newUser.id, displayName: newUser.displayName});
          }
        });
      },
      immediate: true,
      deep: true,
    },
    // isRowsAfterLoadingExist variable updates when new row is added/deleted but should not change when the value of totalRows changes due to filtering.  
    totalRows: {
      handler(newVal) {
        if (Object.values(this.pagedDataParams.filters).every(arr => Array.isArray(arr) && arr.length === 0)) {
          this.isRowsAfterLoadingExist = newVal;
        }
      },
    },
  },
  computed: {
    ...designerMapState({
      modalState: state => state,
      isEditMode: state => state.isEditMode,
    }),
    ...mapStateUsers(['users']),
    ...mapState({
      multiselectRefs: state => state.multiselectRefs,
      defaultLkps: state => state.pagedDataLookups,
      currentUserPermission: state => state.table.schema.currentUserPermissionLevel,
      fieldValues: state => state.fieldValues,
      relationalModals: state => state.table.relationalModals,
      isLookupFieldValuesLoading: state => state.pagedDataLookupValuesLoading,
      isContextMenuOpen: state => state.contextMenu.isContextMenuOpen,
      contextMenuItemId: state => state.contextMenu.contextMenuItemId,
      lookupValues: state => state.lookupValues,
      views: state => state.table.views,
      table: state => state.table.schema.fields,
      tableData: state => state.tableData.data,
      validationRules: state => state.table.validationRules,
    }),
    ...sidebarMapState({
      tablesList: state => state.tablesList,
    }),
    ...mapState(['table', 'permissionTypes', 'validation', 'tables']),
    selectedView() {
      return this.views.find(el => el.isSelected) || {viewGuid: ''};
    },
    canEdit() {
      // allow edit if user has write or higher permissions on the table or on any field
      const editPermissions = [
        this.permissionTypes.admin,
        this.permissionTypes.write,
        this.permissionTypes.delete,
        this.permissionTypes.edit,
        this.permissionTypes.post,
        this.permissionTypes.unpost,
      ];
      return editPermissions.includes(this.currentUserPermission) || this.table.schema.fields.some(f => editPermissions.includes(f.currentUserPermissionLevel));
    },
    canDelete() {
      // allow delete if user has delete or higher permissions on the table or on any field
      const deletePermissions = [
        this.permissionTypes.admin,
        this.permissionTypes.delete,
        this.permissionTypes.edit,
        this.permissionTypes.post,
        this.permissionTypes.unpost,
      ];
      return deletePermissions.includes(this.currentUserPermission) || this.table.schema.fields.some(f => deletePermissions.includes(f.currentUserPermissionLevel));
    },
    canRead() {
      // allow read if user has read permissions on the table or on any field
      return this.permissionTypes.read === this.currentUserPermission || this.table.schema.fields.some(f => f.currentUserPermissionLevel === this.permissionTypes.rea);
    },
    canCheck() {
      return [
        this.permissionTypes.admin,
        this.permissionTypes.write,
        this.permissionTypes.delete,
        this.permissionTypes.edit,
        this.permissionTypes.post,
        this.permissionTypes.unpost,
      ].includes(this.currentUserPermission);
    },

    // ...mapGetters({
    //   selectedView: 'selectedMDMView',
    // }),
    getItems() {
      const currentSubGroupItem = this.fields.find(f => f.fieldGuid === this.subGroupBy);
      const currentGroupItem = this.fields.find(f => f.fieldGuid === this.groupBy);

      const items = this.localItems.map(item => {
        // Add new property 'failedFields' to each item with details of validation failures
        const failedFields = getFailedValidationFields(item, this.validationRules);
        item.failedFields = failedFields;

        item.recordStatus = item[this.validation.status];
        return item;
      });

      if (this.groupBy) {
        const result = [];

        let currentGroup = null;
        let currentSubGroup = null;

        items.forEach(item => {
          const currentItemField = this.isGuid(this.groupBy)
            ? item[currentGroupItem.name]
            : item[this.groupBy];

          if (currentItemField !== currentGroup) {
            result.push({
              [this.isGuid(this.groupBy) ? currentGroupItem.name : this.groupBy]: currentItemField,
              groupName: currentItemField,
              isGroup: true,
              expanded: item.expanded === undefined ? true : item.expanded,
            });
            currentGroup = currentItemField;
            currentSubGroup = null;
          }

          if (this.subGroupBy) {
            const currentItemField = this.isGuid(this.subGroupBy)
              ? item[currentSubGroupItem.name]
              : item[this.subGroupBy];

            if (currentItemField !== currentSubGroup) {
              result.push({
                [this.isGuid(this.groupBy)
                  ? currentSubGroupItem.name : this.groupBy]: currentItemField,
                groupName: currentItemField,
                [this.isGuid(this.subGroupBy)
                  ? currentSubGroupItem.name
                  : this.subGroupBy
                ]: currentItemField,
                subGroupName: currentItemField,
                isSubgroup: true,
                expanded: item.expanded === undefined ? true : item.expanded,
                subGroupExpanded: item.subGroupExpanded === undefined ? true : item.subGroupExpanded,
              });
              currentSubGroup = currentItemField;
            }
          }
          item.groupName = this.isGuid(this.groupBy) ? item[currentGroupItem.name] : item[this.groupBy];
          item.subGroupName = this.isGuid(this.subGroupBy) ? item[currentSubGroupItem.name] : item[this.subGroupBy];
          item.expanded = item.expanded === undefined ? true : item.expanded;
          item.subGroupExpanded = item.subGroupExpanded === undefined ? true : item.subGroupExpanded;
          result.push(item);
        });

        return result;

        //
        // const grouped = _.groupBy(items, (item) => item[this.groupBy] )
        // items = Object.entries(grouped).map(([groupByKey, items]) => ({[this.groupBy]: groupByKey, items}))
        // return items
      }

      return items;
    },
    getTableWidth() {
      return {};
      // let width = 0
      // let columnsWithWidth = 0
      // let tableResponsive = document.getElementsByClassName('table-responsive')
      // let tableWidth = tableResponsive.length ? tableResponsive[0].offsetWidth : 0;
      // this.mergedFields.forEach(f => {
      //   let localWidth = this.columnWidths.find(cw => cw.key === f.key && cw.tableId === f.entityGuid);
      //   if (f.thStyle && f.thStyle.width) {
      //     width += parseInt(f.thStyle.width, 10)
      //     if (localWidth) {
      //       columnsWithWidth++
      //     }
      //   }
      // })
      // width = width && width > tableWidth ? {width: `${width}px`} : columnsWithWidth > 0 ? {width: `auto`} : {width: `100%`}
      // return width
    },
    isValidatedTable() {
      return this.getItems.filter(item => item[this.validation.status] === this.validation.validated).length > 0;
    },
    primaryKeyName() {
      return this.allField.find(f => f.dataTypeName === this.fieldValues.primaryKey).name;
    },
    enabledBulkActions() {
      return this.table.checkedItems.length || Object.keys(this.filters).length;
    },
    groupByField() {
      if (this.groupBy === 'recordStatus')
        return this.recordStatusField;

      return this.getFieldsForTable.find(f => f.name === this.groupBy || f.fieldGuid === this.groupBy) || {};
    },
    subGroupByField() {
      if (this.subGroupBy === 'recordStatus')
        return this.recordStatusField;

      return this.getFieldsForTable.find(f => f.name === this.subGroupBy || f.fieldGuid === this.subGroupBy) || {};
    },
    getFieldsForTable() {
      if (!this.fieldsForTable) return [];
      return this.fieldsForTable.map(el => {
        if ('isIntersecting' in el) {
          return {
            ...el,
            displayInTable: el.isIntersecting,
            stickyColumn: this.stickyColumns.includes(el.key),
          };
        }
        return el;
      });
    },
  },
  methods: {
    ...mapActions([
      'showImportModal',
      'setCheckedItems',
      'unsetUncheckedItem',
      'showAddNewRecordModal',
      'getExportPagedData',
      'saveToggleColumns',
      'closeContextMenu',
      'openContextMenu',
      'resetDefaultOptions',
      'changeTableFieldsOption',
    ]),
    ...mapActionsUser(['getUsersAndGroupsForIds']),
    ...designerMapActions(['updateDataInEditMode', 'setEditRowSectionsModalState', 'getMainDesignerData', 'getAdditionalInfoAboutDesignerFields']),
    setFixedRowHeightState(val) {
      this.styles.limitRowHeight = val;
      this.$emit('limited-height-changed', val);
      // this.isFixedRowHeightEnabled = val
    },
    rlsUserSelect(user, index, item, field) {
      if (!this.allRLSUsers.find(u => u.userGuid === user.userGuid)) {
        this.allRLSUsers = [...this.allRLSUsers, user];
      }
      const existing = this.vModelData[index][field.name];
      // Safely parse or initialize as an array
      let parsed = [];
      try {
        parsed = existing ? JSON.parse(existing) : [];
      } catch (e) {
        console.log('Invalid JSON, resetting to empty array:', existing);
        parsed = [];
      }

      // Add the user.id
      parsed.push(user.id);

      // Save back as stringified JSON
      this.$set(this.vModelData[index], field.name, JSON.stringify(parsed));
    },
    rlsUserRemove(user, index, item, field) {
      const currentUsers = JSON.parse(item[field.name] || '[]');
      const updatedUsers = currentUsers.filter(u => u !== user.id);
      this.vModelData[index][field.name] = JSON.stringify(updatedUsers);
    },
    getValueForTextField(item, field){
      if(field.calculation?.fieldCalculationType === 'AIChat'){
        let parsedItem;
        try {
          parsedItem = JSON.parse(item[field.name]);
        } catch (e) {
          return null;
        }
        // Extract Response and Citations
        const response = parsedItem?.Response;
        const citations = parsedItem?.Citations;

        if (!response || !citations) {
          return null;
        }

        // Replace citation markers with spans
        const transformedResponse = response.replace(/\[(\d+)]/g, (match, citationIndex) => {
          const index = parseInt(citationIndex, 10) - 1; // Adjust for 0-based array
          if (citations[index]) {
            return `<a class="citation-link" title="${citations[index]}" href="${citations[index]}" target="_blank" >${citationIndex}</a>`;
          }
          return match; // Keep original if no matching citation is found
        });
        return transformedResponse;
      }
      else{
        return item[field.name];
      }
    },
    mergedFields() {
      // let tableResponsive = document.getElementsByClassName('table-responsive')
      // let tableWidth = tableResponsive.length ? tableResponsive[0].offsetWidth : 0;

      const fieldsSize = this.localFields.length;
      const internalFields = [];
      let newFields = this.localFields.map(f => {

        const width = this.columnWidths.find(cw => cw.key === f.key && cw.tableId === f.entityGuid);
        let textWidth = pixelWidth(f.displayName, {size: 16});
        const columnAlignment = f.columnAlignment
          ? f.columnAlignment
          : [
            this.fieldValues.wholeNumber,
            this.fieldValues.decimalNumber,
            this.fieldValues.floatNumber,
          ].includes(f.dataTypeName) ? 'right' : 'left';
        textWidth = Math.round(textWidth);

        // Add text alignment to the column
        f = {...f, tdStyle: {textAlign: `${columnAlignment}`}};

        if (width) {
          f = {...f, thStyle: {width: `${width.width}px`}};
        } else if (textWidth <= 120) {
          f = {...f, thStyle: {width: '150px'}};
        } else if (textWidth > 120 && fieldsSize > 1) {
          f = {...f, thStyle: {width: `${textWidth + 30}px`}};
        }
        if (['right', 'center'].includes(columnAlignment)) {
          f.tdStyle.paddingRight = '2.5rem';
          if (!this.sortable) {
            f.thStyle.paddingRight = '1.6rem';
          }
        }
        f.thClass = `text-${columnAlignment}`;
        f.thAttr = {column: f.name};

        if (this.isLoanCalculatorOutput) {
          f.sortable = false;
        } else if (this.isFieldDocument(f)) {
          f.sortable = false;
          // Add border styling for document field headers since they're not sortable
          f.thClass = f.thClass ? `${f.thClass} document-field-header` : 'document-field-header';
        } else {
          f.sortable = this.sortable;
        }

        if (f.isInternal) {
          internalFields.push(f);
          return {};
        }

        f.tdClass = `col-${f.name}`;
        return f;
      });

      newFields = [{
        key: this.fieldValues.selectColumn,
        dataTypeName: this.fieldValues.selectColumn,
        label: '',
        tdClass: 'pl-2 align-top text-left col-selectColumn table-b-table-default b-table-sticky-column',
        thStyle: {width: '96px'},
        thClass: 'pl-3 text-left check-all b-table-sticky-column',
      }, ...newFields, ...internalFields];

      newFields = [...newFields, this.recordStatusField];

      const isActionsVisible = this.canEdit || this.canDelete || this.canRead;
      if (isActionsVisible) {
        newFields = [...newFields, {
          key: 'actions',
          tdClass: 'align-top',
          thStyle: {width: '150px'},
          thClass: 'text-center',
          displayInTable: isActionsVisible,
        }];
      }

      const result = [
        ...newFields.map(f => {
          if (this.groupBy || this.subGroupBy) {
            f.tdAttr = (value, key, item) => {
              return {
                // 'colspan': item.isGroup || item.isSubgroup ? newFields.length : 1,
                'style': (item.isGroup || item.isSubgroup) && key === 'selectColumn' ? 'position: sticky; left: 0; z-index: 40' : '',
              };
            };
          }
          return f;
        }),
      ];

      this.fieldsForTable = result;
    },
    /*setToday() {
      this.displayDate = moment().format('YYYY-MM-DD');
    },*/
    getGuidFromElement(element) {
      return Object.entries(element).find(([key]) => key.length > 4 && key.includes('Guid'))[1];
    },
    onRowClick(item, idx, e) {
      e.preventDefault();
      this.closeContextMenu();
      this.$nextTick(() => {
        if (item.isGroup || item.isSubgroup) return;

        this.currentIdx = idx;
        this.currentItem = item;
        this.top = e.clientY;
        this.left = this.side === 'left' ? e.clientX : e.clientX;
        const currentItemElementFieldId = this.getGuidFromElement(item);
        if (currentItemElementFieldId) {
          item.menuId = currentItemElementFieldId;
          this.openContextMenu(currentItemElementFieldId);
        }
      });
    },
    isBulkUpdateHappened(val) {
      if (this.$refs.table) {
        this.observer = new IntersectionObserver(this.hideShow, {
          root: this.$refs.table.$el,
          threshold: 0.1,
        });
        this.allRows = this.$refs.table.$el.querySelectorAll('th');
        this.allRows.forEach(th => this.observer.observe(th));
      }
    },
    // async searchWithDebounce(field, query, debounceMS = 1000) {
    //   let timeout = this.saveSearchMapForDebounce.get(field.fieldGuid);
    //
    //   if (timeout) {
    //     clearTimeout(timeout);
    //     this.saveSearchMapForDebounce.delete(field.fieldGuid);
    //   }
    //
    //   timeout = setTimeout(async () => {
    //     await this.lookupOptionsFind(field, query);
    //     clearTimeout(timeout);
    //   }, debounceMS);
    //
    //   if (!query.length) {
    //     this.resetDefaultOptions(field.fieldGuid);
    //   }
    //   this.saveSearchMapForDebounce.set(field.fieldGuid, timeout);
    // },
    // formatDecimal(number, decimalPlaces) {
    //   const numString = number.toString();
    //
    //   const [integerPart, decimalPart] = numString.split('.');
    //
    //   const formattedDecimalPart = decimalPart
    //     ? decimalPart.padEnd(decimalPlaces, '0')
    //     : '0'.repeat(decimalPlaces);
    //
    //   return `${integerPart || '0'}.${formattedDecimalPart}`;
    // },
    // async lookupOptionsFind(field, query) {
    //   if(!query.length) return;
    //   const {body} = await this.getPagedDataLookupValueItems({field, query});
    //   const mdmDataTypeJSON = JSON.parse(body.mdmDataTypeJSON);
    //   const dataTypeName = mdmDataTypeJSON.UnderlyingDataType.split('.')[1];
    //
    //   const sourceBackupField = this.backupFields.find(bf => bf.fieldGuid === field.fieldGuid);
    //   const sourceOptions = sourceBackupField.data.options;
    //
    //   let foundOptions;
    //
    //   if (dataTypeName === 'Decimal') {
    //     const scale = mdmDataTypeJSON.Scale;
    //     foundOptions = [...body.data.map(val => ({
    //       id: val.recordGuid,
    //       text: this.formatDecimal(val.value, scale).split(',').join(''),
    //     }))];
    //   } else {
    //     foundOptions = [...body.data.map(val => ({id: val.recordGuid, text: val.value}))];
    //   }
    //
    //   const combinedOptions = sourceOptions.concat(foundOptions);
    //   const uniqCombinedOptions = _.uniqBy(combinedOptions, 'id');
    //   // uniqCombinedOptions.slice(0, 50);
    //   uniqCombinedOptions.sort((a, b) => a.text - b.text);
    //
    //   field.data.options = JSON.parse(JSON.stringify(uniqCombinedOptions));
    // },
    rowClass(item, type) {
      const classes = [];

      if (this.styles.limitRowHeight) {
        classes.push('limited-row-height');
      }

      if (this.isContextMenuOpen) {
        if (item.menuId === this.contextMenuItemId) {
          classes.push('with_context_menu');
          return classes;
        } else {
          if (!item.isSubgroup && !item.isGroup) {
            classes.push('without_context_menu');
          }
        }
      }

      if (item.isGroup) {
        classes.push('group-row');
      }

      if (item.isSubgroup) {
        classes.push('subgroup-row');
      }

      if (!this.groupBy && !this.subGroupBy) {
        return classes.join(' ');
      }

      if (!item.isGroup && !item.expanded) {
        classes.push('d-none');
        return classes.join(' ');
      }

      if ((!item.isSubgroup && !item.isGroup) && !item.subGroupExpanded) {
        classes.push('d-none');
        return classes.join(' ');
      }

      return classes.join(' ');
    },
    rowAttributes(item, type) {
      const attrs = {};
      if (item.groupName) {
        if (typeof item.groupName === 'boolean') {
          attrs['data-group-name'] = item.groupName ? 'true' : 'false';
        } else {
          attrs['data-group-name'] = item.groupName[0].toUpperCase() + item.groupName.substring(1);
        }
      }
      if (item.subGroupName) {
        if (typeof item.subGroupName === 'boolean') {
          attrs['data-subgroup-name'] = item.subGroupName ? 'true' : 'false';
        } else {
          attrs['data-subgroup-name'] = item.subGroupName[0].toUpperCase() + item.subGroupName.substring(1);
        }
      }
      return attrs;
    },
    toggleGroupRows(item) {
      if (!item.isGroup && !item.isSubgroup) {
        return;
      }

      if (item.isGroup) {
        this.localItems = this.localItems.map(it => {
          if (it.groupName === item.groupName) {
            it.expanded = !it.expanded;
          }

          return it;
        });
      } else {
        this.localItems = this.localItems.map(it => {
          if (it.groupName === item.groupName && it.subGroupName === item.subGroupName) {
            it.subGroupExpanded = !it.subGroupExpanded;
          }

          return it;
        });
      }
    },
    removeGrouping(item) {
      this.$refs.table.$el.removeEventListener('scroll', this.onScrollChange);
      if (item.isGroup) {
        this.onGroupByChange({groupBy: this.groupBy, isGrouped: false});
        // this.groupBy = null;
        // this.subGroupBy = null
      } else {
        this.onGroupByChange({groupBy: this.subGroupBy, isGrouped: false});
        // this.subGroupBy = null;
      }
    },
    canEditField(field) {
      return [
        this.permissionTypes.admin,
        this.permissionTypes.write,
        this.permissionTypes.delete,
        this.permissionTypes.post,
        this.permissionTypes.unpost,
        this.permissionTypes.edit,
      ].includes(field.currentUserPermissionLevel);
    },
    isValidSuggestions(suggestionName) {
      if (suggestionName) {
        const suggestions = suggestionName.Suggestions || suggestionName.suggestions;
        return suggestions && suggestions.length > 0;
      }
      return false;
    },
    getMultiSelectValue(item, field, isExport = false) {
      const suggestionName = item[`${field.name}__$LookupStatus`];
      if (!isExport && item[field.name] === null && item.customClass && this.isValidSuggestions(suggestionName)) {
        const suggestions = suggestionName.Suggestions || suggestionName.suggestions;
        return suggestions[0].OriginalValue || suggestions[0].originalValue;
      }
      let currentItem = this.backupFields.find(f => f.name === field.name);
      if (!currentItem) {
        currentItem = this.table.schema.fields.find(f => f.name === field.name);
      }
      let elem = currentItem.data.options.find((i) => {
        return item !== null &&
            i.id === item[field.name]
            || i.id === item[field.name].id;
      });
      if (!elem) {
        elem = this.defaultLkps.find(i => i.id === item[field.name] || i.id === item[field.name].id);
      }
      return elem ? elem.text : '<EMPTY>';
    },
    getRLSUsersAndGroupsForDisplay(item, field) {
      if (
        this.tableData.rlsField === field.fieldGuid &&
        item[field.name] !== '' &&
        this.allRLSUsers.length
      ) {
        try {
          const guids = JSON.parse(item[field.name] || '[]');
          return guids
            .map(guid => {
              const match = this.allRLSUsers.find(user => user.userGuid === guid);
              return match ? match.displayName : guid;
            })
            .join(', ');
        } catch (error) {
          console.error('Failed to parse or map user GUIDs:', error);
          return '';
        }
      }
    },
    getNumberValue(value, field) {
      if (!value && value !== 0) return '';
      const json = field.dataTypeJSON ? JSON.parse(field.dataTypeJSON) : '';
      if (this.isFieldDecimal(field)) {
        value = value || value === 0 ? parseFloat(value).toFixed(json.DecimalsToDisplay) : '';
      }

      return getFormattedNumber(value, json.FormatString, json.DecimalsToDisplay, this.isFieldDecimal(field), json.IsPercentage);
    },
    getSelectText(field, item) {
      const options = field.data && field.data.options ? field.data.options : [];
      const chosenOptions = options.find((elem) => elem.value === item[field.name]);
      if (chosenOptions) {
        return chosenOptions.value ? chosenOptions.text : '';
      }
      return item[field.name];
    },
    getHierarchyText(field, item) {
      // return item[field.name];
      const options = field.data && field.data.options ? field.data.options.items : [];
      const chosenOptions = options.find((elem) => item !== null && elem[field.data.options.hierarchyField.primary.name] === item[field.name]);
      if (chosenOptions) {
        return chosenOptions[field.data.options.hierarchyField.display.name];
      }
      return item ? item[field.name] : '';
    },
    focusOnInput($event, key) {
      this.$refs[`filterInput-${key}`].focus();
    },
    addParamsToLocation(params) {
      this.$router.push({query: params}).catch(() => {
      });
      // history.pushState(
      //     {},
      //     null,
      //     '/#' + this.$route.path +
      //     '?' +
      //     Object.keys(params).map(key => encodeURIComponent(key) + '=' + encodeURIComponent(params[key])).join('&')
      // )
    },
    updateFilters() {
      const currentFilters = this.filters;
      const newFilters = {};
      Object.keys(currentFilters).forEach(fieldName => {
        if (currentFilters[fieldName].fieldType === 'lookup' && !currentFilters[fieldName].selectItems.length) return false;
        newFilters[fieldName] = currentFilters[fieldName];
      });
      this.filters = newFilters;
    },
    onFilter(data) {
      // if (Object.keys(data.updatedFilter).length === 0) {
      //   delete this.$route.query.primaryKey;
      // }
      this.currentPage = 1;
      this.resetScrollData();
      this.filters = data.updatedFilter;
      this.$emit('filter-changed', data.query.filter);
      if (this.$route.query.primaryKey) {
        this.$emit('filter-primary-key', {[this.table.schema.fields.filter(f => f.isPrimaryKey)[0].name]: JSON.parse(this.$route.query.primaryKey)});
      }

      this.updateFilters();

      const queryParams = {};
      Object.keys(data.query.filter).map((key) => {
        queryParams[key] = {
          fieldType: data.query.filter[key].fieldType,
          label: data.query.filter[key].label,
        };
        if (this.isFieldNumber(data.query.filter[key]) || this.isFieldText(data.query.filter[key])) {
          queryParams[key] = {
            ...queryParams[key],
            text: data.query.filter[key].text,
            type: data.query.filter[key].type,
          };
        }
        if (this.isMultiSelect(data.query.filter[key])) {
          const selectedItems = data.query.filter[key].selectItems;
          if (selectedItems.length) {
            queryParams[key] = {
              ...queryParams[key],
              selectItems: selectedItems,
            };
          } else {
            delete queryParams[key];
          }
        }
        if (this.isFieldHierarchy(data.query.filter[key])) {
          queryParams[key] = {
            ...queryParams[key],
            field: data.query.filter[key].field,
            selectItems: data.query.filter[key].selectItems,
          };
        }
        if (this.isFieldDatetime(data.query.filter[key]) || this.isFieldDate(data.query.filter[key])) {
          queryParams[key] = {
            ...queryParams[key],
            fromDate: data.query.filter[key].fromDate,
            toDate: data.query.filter[key].toDate,
          };
        }
        if (this.isFieldCheckbox(data.query.filter[key])) {
          queryParams[key] = {
            ...queryParams[key],
            selected: data.query.filter[key].selected,
          };
        }
        if (this.isFieldRecordStatus(data.query.filter[key])) {
          queryParams[key] = {
            ...queryParams[key],
            selectedStatus: data.query.filter[key].selectedStatus,
          };
        }
        if (this.isFieldPostedStatus(data.query.filter[key].fieldType)) {
          queryParams[key] = {
            ...queryParams[key],
            selectedPostedStatuses: data.query.filter[key].selectedPostedStatuses,
          };
        }
        if (this.isSelectColumn(data.query.filter[key])) {
          queryParams[key] = {
            ...queryParams[key],
            createdDate: data.query.filter[key].createdDate,
            updatedDate: data.query.filter[key].updatedDate,
          };
        }
      });
      if (!Object.keys(this.filters).length) {
        const query = {...this.$route.query};
        delete query.filters;
        this.addParamsToLocation(query);
      }
      // this.addParamsToLocation({...this.$route.query, filters: JSON.stringify(queryParams)});
    },
    resetScrollData() {
      const tableResponsive = document.getElementsByClassName('table-responsive');
      if (tableResponsive.length > 0) {
        const resData = tableResponsive[0];
        resData.scrollTop = 0;
        resData.scrollLeft = 0;
      }
    },
    failedItem(item) {
      if (item[this.validation.status] === this.validation.validationFailed) {
        return item[this.validation.warnings];
      }
    },
    isValidField(item) {
      return item[this.validation.status] === this.validation.validated;
    },
    onCheckboxChange(value, index, item, field) {
      item[field.name] = value;
      this.clearCellEdit();
      this.$emit('cell-edit-finish', value, !value, index, item, field);
      // this.finishEditing(null, index, item, field);
    },
    onEnterKey(e, index, item, field) {
      if (e.key === 'Enter' && !e.shiftKey) {
        if (this.editingCell.click === 1) {
          this.onClick(e, this.editingCell.index, this.editingCell.item, this.editingCell.field);
        } else {
          e.target.blur();
        }
      }
    },
    onClick(e, index, item, field) {
      if (this.isCalculatedLookup(field)) {
        return;
      }
      if (this.editingCell.click !== 2) {
        this.selectCell(index, item, field);
      }
      if (this.editingCell.click === 2) {
        this.startCellEditing(e, index, item, field);
      }
    },
    selectCell(index, item, field) {
      let click = this.editingCell.click ? this.editingCell.click : 0;
      if (
        click === 1
          && !(this.editingCell.field.ordinalPosition === field.ordinalPosition
              && this.editingCell.rowIndex === index)
      ) {
        click = 1;
      } else if (click === 1
          && (this.isFieldCheckbox(field)
              || this.isFieldDocument(field)
              || this.isFieldHierarchyNotTree(field)
              || (this.isPostedStatus(field) && !this.canEditPostedStatus())
              || (!this.isPostedStatus(field) && this.hasPostedValue(item)))
      ) {
        click = 0;
      } else {
        ++click;
      }
      this.setCellEdit(index, field, item, click);
    },
    nextRow(e, index, item, field) {
      index = this.editingCell.index;
      field = this.editingCell.field;
      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        let changedIndex = index;
        if (e.key === 'ArrowUp') {
          changedIndex--;
          if (changedIndex < 0) {
            changedIndex = this.totalRows > this.perPage ? this.perPage - 1 : this.totalRows - 1;
          }
        } else if (e.key === 'ArrowDown') {
          changedIndex++;
          if (changedIndex > this.perPage - 1 && this.totalRows > this.perPage || changedIndex > this.totalRows - 1 && this.totalRows < this.perPage) {
            changedIndex = 0;
          }
        }
        this.setCellEdit(changedIndex, field, this.items[changedIndex], 1);
      }
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        let columnIndex = this.editingCell.field.ordinalPosition;
        if (e.key === 'ArrowLeft') {
          columnIndex--;
          if (columnIndex < 0) {
            columnIndex = this.fields.length - 1;
          }
        } else if (e.key === 'ArrowRight') {
          columnIndex++;
          if (columnIndex > this.fields.length - 1) {
            columnIndex = 0;
          }
        }
        const newField = this.fields[columnIndex];
        this.setCellEdit(index, newField, this.items[index], 1);
      }
      e.preventDefault();
    },
    isGuid(value) {
      const guidRegExp = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      return guidRegExp.test(value);
    },
    async setCellEdit(index, field, item, click) {
      this.vModelData[index] = {...this.vModelData[index], ...{[field.name]: item[field.name]}};
      this.editingCell = {
        rowIndex: index,
        columnKey: field.name,
        index,
        item,
        field,
        click,
      };

      if (field.dataTypeName === 'lookup' && this.isInEditing(index, field)) {
        this.isMultiselectActivated = true;
      }
    },
    clearCellEdit() {
      this.editingCell = {
        rowIndex: null,
        columnKey: null,
        index: null,
        item: null,
        field: null,
        click: 0,
      };
      this.customDateChange = {
        inputDate: '',
        inputEdit: false,
      };
    },
    /* --------------------------------------------------- */
    /* Methods relating to document field */
    
    // Extracts and parses document data from a document field value.
    getDocumentDataFromField(item, field) {
      const documentFieldValue = item[field.name];
      let documents = [];

      if (documentFieldValue) {
        if (Array.isArray(documentFieldValue)) {
          documents = documentFieldValue;
        } else {
          try {
            const parsed = JSON.parse(documentFieldValue);
            documents = Array.isArray(parsed) ? parsed : [parsed];
          } catch (error) {
            documents = [];
          }
        }
      }
      return documents;
    },
    // Generates a tooltip for document fields, showing document names.
    getDocumentTooltip(item, field) {
      const documents = this.getDocumentDataFromField(item, field);
      if (!documents?.length) {
        return 'No documents available';
      }
      return documents.map(doc => doc.name).join(', ');
    },
    // Get Font Awesome icon based on file extension
    getFileTypeIconForTemplate(fileName) {
      return getFileTypeIcon(fileName);
    },
    // Get the colour style for file type icons
    getFileTypeColourForTemplate(fileName) {
      return getFileTypeColour(fileName);
    },
    // Prepares document data and opens document preview for a given cell
    async openDocumentPreview(item, field) {
      this.documentPreviewData = this.getDocumentDataFromField(item, field);

      // Store the item and field for later use in the preview modal
      this.itemInDocumentPreview = item;
      this.fieldInDocumentPreview = field;

      // Initialise designer state if table schema is available
      const tableSchema = this.$store.state.dataManagement.table.schema;
      if (tableSchema && tableSchema.addEditRowSectionsJSON) {
        await this.getMainDesignerData(tableSchema.addEditRowSectionsJSON);
        await this.getAdditionalInfoAboutDesignerFields(tableSchema.fields);
      }
      // Set up edit mode for the item (without opening the EditSections modal)
      this.setEditRowSectionsModalState({
        show: false, 
        isEditMode: true,
        item: item,
      });

      this.showDocumentPreviewModal = true;
    },
    // Handles cleanup when the document preview modal is closed.
    onDocumentPreviewModalHidden() {
      this.showDocumentPreviewModal = false;
      this.documentPreviewData = [];
      this.itemInDocumentPreview = null;
      this.fieldInDocumentPreview = null;

      this.setEditRowSectionsModalState({
        show: false,
        isEditMode: false,
        item: null,
      });
    },
    // Updates the document preview data for changes to the item if document preview modal is open
    updateDocumentPreviewData(item, field) {
      this.documentPreviewData = this.getDocumentDataFromField(item, field);
    },
    /* --------------------------------------------------- */
    onSelectValue(node, index, item, field) {
      this.closeTreeSelect = false;
      setTimeout(() => {
        if (!node.disabled) {
          this.closeTreeSelect = true;
          this.finishEditing(node, index, item, field);
        }
      }, 10);
    },
    onClearData(event, index, item, field) {
      if (event === undefined) {
        if (!field.isRequired) {
          this.finishEditing(event, index, item, field);
        } else {
          this.$toasted.error('Field is required', {
            duration: TOASTER_DURATION,
          });
        }
      }
    },
    onCloseEvent(e, field) {
      this.isMultiselectActivated = false;
      this.resetDefaultOptions(field.fieldGuid);
      this.closeTreeSelect = false;
      this.editingCell.click = 0;
    },
    onDatePickerInputChange() {
      this.vModelData = [...this.vModelData];
    },
    onDatePickerOpen(event, index, item, field) {
      // Add enter keydown event listener for date picker
      setTimeout(() => {
        this.addDatePickerKeydownListener(index, item, field);
      }, 50);

      event.target.addEventListener('click', () => {
        this.addDatePickerKeydownListener(index, item, field);
      });
    },
    addDatePickerKeydownListener(index, item, field) {
      const calendarPanel = document.getElementsByClassName('mx-datepicker-main mx-datepicker-popup');
      if (calendarPanel[0]) {
        const datePicker = calendarPanel[0];
        datePicker.tabIndex = 0;
        datePicker.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            this.finishEditing(e, index, item, field);
          }
          if (e.key === 'Escape') {
            this.cancelEditing(e, index, item, field);
          }
        });
      }
    },
    datePickerEvents(e, index, item, field) {
      const datePicker = document.getElementById(`currentInput_${index}_${field.ordinalPosition}`);
      if (datePicker) {
        const dateInput = datePicker.children[0].children[0];
        const currentInput = this.$refs[`currentInput_${index}_${field.ordinalPosition}`];

        // Add enter and escape keydown event listener for input
        dateInput.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') {
            this.cancelEditing(e, index, item, field);
          }
          if (e.key === 'Enter') {
            this.finishEditing(e, index, item, field);
          }
        });

        dateInput.addEventListener('input', () => {
          this.customDateChange = {
            inputEdit: true,
            inputDate: currentInput.text,
            [`open_${index}_${field.ordinalPosition}`]: false,
          };
        });

        let isInvalidDate = false;
        dateInput.addEventListener('click', () => {
          // Update model value after change from input
          if (this.customDateChange.inputEdit) {
            const formattedDate = this.getFormattedDate(field, currentInput.text);
            if (this.isInvalidDate(formattedDate)) {
              isInvalidDate = true;
            } else {
              isInvalidDate = false;
              this.vModelData[index][field.name] = moment(formattedDate).format(this.getDateFormat(field));
            }
            this.onDatePickerInputChange();
          }
          if (!isInvalidDate) {
            this.customDateChange = {
              inputEdit: false,
              inputDate: '',
              [`open_${index}_${field.ordinalPosition}`]: true,
            };
          } else {
            this.$toasted.error(`Invalid Date. Date format must be "${this.getDateFormat(field)}"`, {
              duration: TOASTER_DURATION,
            });
          }
        });

        dateInput.addEventListener('blur', (e) => {
          if (this.customDateChange.inputEdit) {
            this.finishEditing(e, index, item, field);
          }
        });
      }
    },
    finishLkpEditing(content, oldContent, index, item, field) {
      item[field.name] = content.id;
      this.changeTableFieldsOption({content, field});
      this.$emit('cell-edit-finish', content, oldContent, index, item, field);
      this.clearCellEdit();
    },
    async startCellEditing(e, index, item, field) {
      this.lineBreakTextAreaHeight = 50;
      const content = this.$refs[`content_${index}_${field.ordinalPosition}`];
      if (this.readOnly) {
        this.clearCellEdit();
        return;
      }

      if (![
        'text',
        'decimalNumber',
        'floatNumber',
        'wholeNumber',
        'dateTime',
        'lookup',
        'date',
      ].includes(field.dataTypeName)) {
        return;
      }

      if (!this.canEditField(field) || field.isCalculated) {
        this.clearCellEdit();
        return;
      }

      // // If some other cell is in edit process we need to finish it
      // if (this.editingCell.rowIndex) {
      //   const {index, item, field} = this.editingCell;
      //   this.finishEditing(e, index, item, field);
      // }
      this.$emit('cell-edit-start', index, item, field);

      if (!this.vModelData[index]) {
        this.vModelData[index] = {};
      }

      if (field.dataTypeName === 'lookup') {
        const suggestionName = item[`${field.name}__$LookupStatus`];
        if (item[field.name] === null && item.customClass && this.isValidSuggestions(suggestionName)) {
          const suggestions = suggestionName.Suggestions || suggestionName.suggestions;
          this.vModelData[index][field.name] = {
            id: null,
            text: suggestions[0].OriginalValue || suggestions[0].originalValue,
          };
        } else {
          const foundItem = field.data.options.find((elem) => elem.id === item[field.name]) || {};
          this.vModelData[index][field.name] = JSON.parse(JSON.stringify(foundItem));
        }
        // this.$refs[`multi-select-${field.name}-${index}`].activate();
      } else if (this.isPostedStatus(field)) {
        const foundItem = field.data.options.find((elem) => elem.value === item[field.name]) || {};
        this.vModelData[index][field.name] = JSON.parse(JSON.stringify(foundItem));
      } else if (this.isFieldDate(field) || this.isFieldDatetime(field)) {
        this.vModelData[index][field.name] = item[field.name] ? moment(item[field.name]).format(this.getDateFormat(field)) : null;
        // Add events for date picker
        this.datePickerEvents(e, index, item, field);
      } else if (this.isFieldText(field) && item[field.name]) {
        let textItemValue;
        if (this.isLineBreakTextField(field)) {
          if (content.offsetHeight > this.lineBreakTextAreaHeight) {
            this.lineBreakTextAreaHeight = content.offsetHeight;
          }
          textItemValue = item[field.name];
        } else {
          textItemValue = item[field.name].replace(/\s\s+/g, ' ');
        }
        this.vModelData[index][field.name] = textItemValue.trim();
      } else {
        this.vModelData[index][field.name] = item[field.name];
      }

      setTimeout(() => {
        const input = this.$refs[`currentInput_${index}_${field.ordinalPosition}`];
        if (input) input.focus();

        //manually clicking on edit users, to open popover 
        // - needs revision once we refactor table 
        // - it is impossible to do show porp, as component is mounted on focus, not on render - would spend too much time
        // and this is needed, as also blure is not possible on div element, so it wont finish editing if you dont open popover - which would be confusing for the user
        const el = document.getElementById(`usersPopoverButtonrow-${index}`);
        if (el) {
          const event = new MouseEvent('click', {
            view: window,
            bubbles: true,
            cancelable: true,
          });
          el.dispatchEvent(event);
        }
      }, 50);
    },
    finishEditing(e, index, item, field) {
      const oldItem = JSON.parse(JSON.stringify(item));
      this.closeTreeSelect = false;
      setTimeout(() => { //To catch changed multi-select value.
        // this method will be called even when we hit ESC key on keyboard because it triggers blur event
        // if ESC was pressed this.editingCell will be reseted. We check if this.editingCell does not have index
        // we skip finishEditing
        if (this.editingCell.index === null || this.editingCell.index === undefined) {
          return;
        }

        let oldContent;
        const content = this.customDateChange.inputEdit
          ? this.customDateChange.inputDate
          : this.vModelData[index][field.name];

        let isInvalidDate = false;
        if (this.isPostedStatus(field)) {
          item[field.name] = content.value;
          oldContent = oldItem[field.name];
          this.$emit('cell-edit-finish', content, oldContent, index, item, field);
        } else {
          oldContent = item[field.name];
          if (content !== oldContent) {
            if ((this.isFieldDate(field) || this.isFieldDatetime(field)) && content) {
              item[field.name] = this.getFormattedDate(field, content);

              if (this.isInvalidDate(item[field.name])) {
                isInvalidDate = true;
                this.$toasted.error(`Invalid Date. Date format must be "${this.getDateFormat(field)}"`, {
                  duration: TOASTER_DURATION,
                });
                item[field.name] = oldContent;
              }
            } else {
              item[field.name] = content;
            }
            if (!isInvalidDate) {
              this.$emit('cell-edit-finish', content, oldContent, index, item, field);
            }
          }
        }


        // When finishEditing is called there might be other cell already in edit process
        // We check right here if the exact same cell is in edit process on which we call finishEditing
        // In this case we disable editing
        // if (index === this.editingCell.index && field === this.editingCell.field) {
        this.clearCellEdit();
        // }
      });

    },
    cancelEditing(e, index, item, field) {
      delete this.vModelData[index][field.name];
      this.clearCellEdit();
      this.$emit('cell-edit-cancel', index, item, field);
    },
    selectChange(content, index, item, field) {
      const oldContent = item[field.name];
      // if the field is select we take the value, otherwise we take the original content
      if (field.type === 'select') {
        item[field.name] = content.value;
      } else {
        item[field.name] = content;
      }
      this.localItems = [...this.localItems];
      if (content !== oldContent) {
        this.$emit('cell-edit-finish', content, oldContent, index, item, field);
      }
    },
    getFieldByName(name) {
      return this.fields.find(el => el.name === name);
    },
    onSortChange(sorting, afterRemoveParticular = false) {

      // All Sorting was removed
      if (sorting.length === 0) {
        this.sortBy = null;
        this.sortDesc = null;
      } else {
        for (const sort of sorting) {
          this.sortBy = sort.FieldNameOrGuid;
          this.sortDesc = !sort.ascending;
        }
      }

      this.$emit('sort-changed', sorting, afterRemoveParticular);
    },
    onSortChanged({sortBy, sortDesc}) {
      this.closeContextMenu();

      const currentField = this.getFieldByName(sortBy);
      let sortCriterion = sortBy;
      if (sortBy === this.fieldValues.recordStatus) {
        sortCriterion = '__validationStatus__';
      }

      this.$emit('sort-changed', [{
        FieldNameOrGuid: currentField.fieldGuid,
        ascending: !sortDesc,
      }]);

      this.sortBy = sortBy;
      this.sortDesc = sortDesc;
    },
    onScrollChange() {
      if (!this.totalRows || !this.$refs.table) {
        return;
      }
      const scrollLeft = this.$refs.table.$el.scrollLeft;
      const scrollTop = this.$refs.table.$el.scrollTop;
      this.onOutsideClick();
      let i = 0;
      let sumOfPreviousTds = 0;
      for (i = 0; i < this.stickyColumns.length; i++) {
        const col = this.stickyColumns[i];
        const tds = this.$refs.table.$el.querySelectorAll(`td.b-table-sticky-column.col-${col}, th.b-table-sticky-column[column=${col}]`);
        const previousTd = this.$refs.table.$el.querySelector(`td.b-table-sticky-column.col-${this.stickyColumns[i]}`);
        sumOfPreviousTds += previousTd.offsetWidth;
        if (previousTd.offsetLeft + previousTd.offsetWidth - 1 > tds[0].offsetLeft) {
          tds.forEach(td => {
            if (td.classList.contains('col-selectColumn')) {
              td.style.left = 0 + 'px';
            } else {
              td.style.left = (sumOfPreviousTds - 186) + 'px';
            }
          });
        }

        if (scrollLeft + sumOfPreviousTds < tds[0].offsetLeft) {
          tds.forEach(td => td.style.left = '');
        }
      }

      const allRows = this.$refs.table.$el.querySelectorAll('tr');

      // allRows.forEach(tr => {
      //   tr.style.cssText = '';
      // })
      if (this.groupBy) {
        const headerHeight = this.$refs.table.$el.querySelector('th.b-table-sticky-column').offsetHeight;
        const groupRowTrs = this.$refs.table.$el.querySelectorAll('tr.group-row');

        const css = `position: sticky; z-index: 3; background-color: #e8e8e8; top: ${headerHeight}px;`;
        groupRowTrs[0].style.cssText = css;

        groupRowTrs.forEach(tr => {
          if (scrollTop < tr.offsetTop) {
            tr.style.cssText = css;
          }
        });

        if (this.subGroupBy) {
          const subGroupRowTrs = this.$refs.table.$el.querySelectorAll('tr.subgroup-row');

          const css = `position: sticky; z-index: 3; background-color: #e8e8e8; top: ${headerHeight + subGroupRowTrs[0].offsetHeight}px;`;
          subGroupRowTrs[0].style.cssText = css;

          subGroupRowTrs.forEach(tr => {
            if (scrollTop < tr.offsetTop) {
              tr.style.cssText = css;
            }
          });
        }

      }
    },
    onGroupByChange({groupBy, isGrouped}) {
      this.$emit('group-by-changed', {groupBy, isGrouped});
    },
    onColumnFreeze(field) {
      if (this.stickyColumns.includes(field.key)) {
        this.stickyColumns = this.stickyColumns.filter(col => col !== field.key);
        // Remove style.left from td and th, at the end of the function they will be recalculated
        const tds = this.$refs.table.$el.querySelectorAll('td.b-table-sticky-column, th.b-table-sticky-column');
        tds.forEach(td => td.style.left = '');
      } else {
        this.stickyColumns = [...this.stickyColumns, field.key];
      }

      const nameToPositionFieldMap = new Map();
      this.fields.forEach((f, ind) => nameToPositionFieldMap.set(f.name, ind));

      this.stickyColumns = this.stickyColumns.sort((a, b) => {
        const positionA = nameToPositionFieldMap.get(a);
        const positionB = nameToPositionFieldMap.get(b);

        if (positionA < positionB) {
          return -1;
        } else if (positionA > positionB) {
          return 1;
        } else {
          return 0;
        }
      });

      this.$nextTick(() => {
        this.onScrollChange();
      });

      this.$emit('on-freeze-columns-change', this.stickyColumns);
    },
    onColumnResize(event, field) {
      let th = event.target.parentElement;
      const table = document.getElementsByTagName('table')[0];
      const tableHead = document.getElementsByTagName('thead')[0];
      let moving = false;
      if (th.nodeName !== 'TH') {
        th = th.parentElement;
      }
      if (th.nodeName === 'TH') {
        let textWidth = pixelWidth(field.displayName, {size: 16});
        textWidth = Math.round(textWidth);
        th.style.minWidth = `${textWidth + 40}px`;
        tableHead.style.pointerEvents = 'none';
        tableHead.style.userSelect = 'none';
        table.style.cursor = 'col-resize';
        moving = true;

        const cutPx = str => +str.replace('px', '');

        table.addEventListener('mousemove', (e) => {
          if (moving) {
            th.style.width = th.offsetWidth + e.movementX + 'px';
            table.style.width = table.offsetWidth + e.movementX + 'px';
            if (cutPx(th.style.width) < cutPx(th.style.minWidth)) {
              moving = false;
              th.style.width = th.style.minWidth;
            }
            moving = true;
          }
        });

        document.addEventListener('mouseup', () => {
          if (!moving) return;

          moving = false;
          tableHead.style.pointerEvents = '';
          table.style.cursor = 'default';

          const thElem = document.getElementsByTagName('th');
          for (let i = 0; i < thElem.length; i++) {
            const field = this.fieldsForTable;
            const currentField = field[i];
            if (currentField && !this.isStaticColumn(currentField)) {
              const index = this.columnWidths.map(cw => cw.key).indexOf(currentField.key);
              const currentTable = this.columnWidths.map(cw => cw.tableId).indexOf(currentField.entityGuid);
              if (index === -1 || currentTable === -1) {
                this.columnWidths.push({
                  key: currentField.key,
                  width: thElem[i].clientWidth,
                  tableId: currentField.entityGuid,
                });
              } else {
                this.columnWidths[index].width = thElem[i].clientWidth;
              }
              localStorage.setItem('RESIZE', JSON.stringify(this.columnWidths));
            }
          }
        });
      }
    },
    isStaticColumn(field) {
      return [
        'actions',
        this.fieldValues.recordStatus,
        this.fieldValues.selectColumn,
      ].includes(field.key);
    },
    showFilterDropdown(data) {
      // return data.
      return !['actions'].includes(data.field.key) && !this.isFieldDocument(data.field);
    },
    onEditClick(item, index, field) {
      if (this.readOnly) return false;
      this.$emit('on-edit', item, index, field);
    },
    onConfirmClick(item, index, field) {
      if (this.readOnly) return false;
      this.$emit('on-confirm', item, index, field);
    },
    onAddNewRelationalEntity(item, index, field) {
      this.$emit('on-add-new-relational-entity', item, index, field);
    },
    getDateFormat(field) {
      const jsonData = JSON.parse(field.dataTypeJSON);
      const format = jsonData.FormatString ? jsonData.FormatString : 'YYYY-MM-DD';

      return `${format}${this.isFieldDate(field) ? '' : ' HH:mm:ss'}`;
    },
    getFormattedDate(field, content) {
      const dateFormat = this.getDateFormat(field);
      const defaultFormat = 'YYYY-MM-DD';
      return this.isFieldDate(field) ?
        this.$options.filters.toDate(moment(content, dateFormat).toDate(), defaultFormat) :
        this.$options.filters.toDatetime(moment(content, dateFormat).toDate(), `${defaultFormat} HH:mm:ss`);
    },
    async onDeleteClick(item, index, field) {
      if (this.hasPostedValue(item)) return false;
      this.$emit('on-delete', item, index, field);
    },
    async onBulkDelete() {
      this.$emit('on-bulk-delete');
    },
    onHistoryClick(item, index, field) {
      this.$emit('on-history', item, index, field);
    },
    onPerPageChange() {
      this.currentPage = 1;
      this.resetScrollData();

      this.$emit('on-pagination-change', {perPage: this.perPage, currentPage: this.currentPage});
    },
    onCurrentPageChange() {
      this.resetScrollData();

      this.$emit('on-pagination-change', {perPage: this.perPage, currentPage: this.currentPage});
    },
    async onChangedRow(result) {   
      if (result.insertData.ResultSetTemp.length > 0) {
        if (!result.inline) {
          const item = result.insertData.updatedRecord;
          this.$emit('changed-row', item);
        } else if (result.insertData.status) {

          this.fields.forEach(async (field) => {
            if (field.relationalTableId === result.insertData.entityGuid) {
              const {body} = await axiosClient.get(`api/field/${JSON.parse(field.dataTypeJSON).FieldToDisplayGuid}`);
              const newOption = {
                id: result.updatedId,
                text: result.insertData.updatedRecord[body.name],
              };
              if(this.modalState.isModalOpened){               
                if(field.name === result.field.name){
                  await this.updateDataInEditMode({field, data: newOption, isEditMode: this.isEditMode});
                  return;
                }
              }else{
                this.$nextTick(() => {            
                  this.multiselectRefs[`lkp-multiselect-${result.field.name}-${result.rowIndex + 1}`].select(newOption);
                });
              }
              

            }
            return field;
          });
        }
      }
    },
    addNewEntity(field, newOption) {
      this.$emit('add-new-entity', field, newOption);
    },
    async exportData() {
      this.tableLoading = true;

      let items;
      if (this.table.checkedItems.length) {
        items = this.table.checkedItems;
      } else {
        const params = {
          id: this.$route.params.tableId,
          ...this.pagedDataParams,
          pageNumber: 1,
          pageSize: 0,
        };

        const {success, body} = await this.getExportPagedData(params);

        if (success) {
          items = body.Records;
        } else {
          this.$toasted.error(body.ErrorMessage, {
            duration: TOASTER_DURATION,
          });
        }
      }

      let fields = JSON.parse(JSON.stringify(this.allField));

      const index = fields.findIndex(f => f.isInternal);
      fields.splice(index, 0, this.recordStatusField);

      fields = fields.filter(f => !f.isRLSField);

      const header = fields.map((elem) => elem.displayName || elem.label); // columns name

      const customFields = {
        requiredFields: {},
        dateFields: {},
        dateTimeFields: {},
        lineBreakTextFields: {},
        wholeNumberFields: {
          separation: {},
          nonSeparation: {},
        },
        decimalFields: {
          separation: {},
          nonSeparation: {},
        },
      };
      fields.forEach(f => {
        const json = f.dataTypeJSON ? JSON.parse(f.dataTypeJSON) : {};
        const fieldName = (f.isPrimaryKey ? f.name : f.displayName) || f.label;
        if (f.isRequired && !f.isInternal) {
          customFields.requiredFields[fieldName] = f;
        }
        if (json.IsLineBreak) {
          customFields.lineBreakTextFields[fieldName] = f;
        }
        if (this.isFieldDate(f)) {
          customFields.dateFields[fieldName] = f;
        }
        if (this.isFieldDatetime(f)) {
          customFields.dateTimeFields[fieldName] = f;
        }
        if (['comma', 'dot'].includes(json.FormatString)) {
          if (this.isFieldWholeNumber(f)) {
            customFields.wholeNumberFields.separation[fieldName] = f;
          }
          if (this.isFieldDecimal(f)) {
            customFields.decimalFields.separation[fieldName] = f;
          }
        } else {
          if (this.isFieldWholeNumber(f)) {
            customFields.wholeNumberFields.nonSeparation[fieldName] = f;
          }
          if (this.isFieldDecimal(f)) {
            customFields.decimalFields.nonSeparation[fieldName] = f;
          }
        }
      });

      const exportData = items.map((item) => {
        const result = {};
        fields.forEach((f) => {
          const fieldName = (f.isPrimaryKey ? f.name : f.displayName) || f.label;
          let val = item[this.getFormattedFieldName(f.name, f.key, true)];
          const jsonData = f.dataTypeJSON ? JSON.parse(f.dataTypeJSON) : f.dataTypeJSON;
          if (this.isFieldRecordStatus(f)) {
            val = this.getStatusValue(item);
          } else if (this.isFieldWholeNumber(f)) {
            const parsedInt = parseInt(val);
            val = !parsedInt && parsedInt !== 0 ? '' : parsedInt;
          } else if (this.isFieldDecimal(f)) {
            const parsedFloat = parseFloat(val);
            val = !parsedFloat && parsedFloat !== 0 ? '' : parsedFloat;
            if (jsonData.IsPercentage && (val || val === 0)) {
              val = val / 100;
            }
          }
          /* else if (this.isFieldDatetime(f)) {
            val = this.$options.filters.toDatetime(val, 'YYYY/MM/DD HH:mm:ss');
          } else if (this.isFieldDate(f)) {
            val = this.$options.filters.toDate(val, 'YYYY/MM/DD');
          }*/
          else if (this.isFieldDate(f)) {
            // Date.parse(val) -> date to timestamp
            // 8.64e7 -> 1 day
            const days = Math.round((Date.parse(val) - new Date(1899, 11, 30)) / 8.64e7);
            const date = parseInt((days).toFixed(10));

            val = isNaN(date) ? null : date;
          } else if (this.isFieldDatetime(f)) {
            // date to timestamp / 1day + 1970/01/01
            const date = moment.utc(val).valueOf() / 8.64e7 + 25569;
            val = isNaN(date) ? null : date;
          } else if (this.isFieldCheckbox(f)) {
            val = val ? 'True' : 'False';
          } else if (this.isPostedStatus(f)) {
            val = this.getSelectText(f, item);
          } else if (this.isFieldHierarchy(f)) {
            val = this.getHierarchyText(f, item);
          } else if (this.isMultiSelect(f)) {
            val = this.getMultiSelectValue(item, f, true);
          }

          result[fieldName] = val;
        });
        result['Record Status'] = this.getStatusValue(item);
        return result;
      });

      const result = {};
      fields.forEach((f) => {
        const fieldName = (f.isPrimaryKey ? f.name : f.displayName) || f.label;
        result[fieldName] = null;
      });
      if (exportData.length === 0) {
        exportData.push(result);
      }

      const params = {
        header,
        exportData,
        customFields,
      };

      this.tableLoading = false;
      this.$emit('on-export', params);
    },
    showImport(type) {
      this.importType = type;
      this.importFields = this.allField;
      this.showImportModal();
    },
    isInEditing(index, field) {
      return this.editingCell.rowIndex === index && this.editingCell.columnKey === field.name
          && this.editingCell.click === 2 && !this.isFieldCheckbox(this.editingCell.field);
    },
    isFirstClick(index, field) {
      return this.editingCell.rowIndex === index && this.editingCell.columnKey === field.name && this.editingCell.click === 1;
    },
    isSearchInput(field) {
      const dataTypeJSON = JSON.parse(field.dataTypeJSON);
      return [this.fieldValues.text].includes(field.dataTypeName)
          && field.dataTypeName === this.fieldValues.text
          && dataTypeJSON.UsePredictiveSearch;
    },
    isFieldText(field) {
      const type = field.fieldType || field.dataTypeName;
      return type === 'text' || type === 'primaryKey' || type === 'lastUpdatedBy';
    },
    isFieldEmail(field) {
      return field.dataTypeName === 'email';
    },
    isFieldNumber(field) {
      const type = field.fieldType || field.dataTypeName;
      return type === 'decimalNumber' || type === 'wholeNumber' || type === 'floatNumber';
    },
    isFieldDecimal(field) {
      const type = field.fieldType || field.dataTypeName;
      return type === this.fieldValues.decimalNumber || type === this.fieldValues.floatNumber;
    },
    isFieldWholeNumber(field) {
      const type = field.fieldType || field.dataTypeName;
      return type === this.fieldValues.wholeNumber;
    },
    isFieldDate(field) {
      const type = field.fieldType || field.dataTypeName;
      return type === 'date';
    },
    isFieldDatetime(field) {
      const type = field.fieldType || field.dataTypeName;
      return [
        this.fieldValues.dateTime,
        this.fieldValues.createdDate,
        this.fieldValues.lastUpdatedDate,
      ].includes(type);
    },
    isSelectColumn(field) {
      const type = field.fieldType || field.dataTypeName;
      return type === this.fieldValues.selectColumn;
    },
    isMultiSelect(field) {
      const type = field.fieldType || field.dataTypeName;
      return type === 'lookup';
    },
    isRLSField(field) {
      return this.tableData.rlsField==field.fieldGuid;
    },
    isHierarchyTree(field) {
      const type = field.fieldType || field.dataTypeName;
      const jsonData = typeof field.dataTypeJSON === 'string' ? JSON.parse(field.dataTypeJSON) : field.dataTypeJSON;
      return type === this.fieldValues.parentChildHierarchy && jsonData.ControlType === 'tree';
    },
    isFieldCheckbox(field) {
      const type = field.fieldType || field.dataTypeName;
      return type === 'boolean';
    },
    isFieldDocument(field) {
      const type = field.fieldType || field.dataTypeName;
      return type === 'document';
    },
    isFieldRecordStatus(field) {
      const type = field.fieldType || field.key;
      return type === this.fieldValues.recordStatus;
    },
    isFieldSelect(field) {
      return false;
      // return field.dataTypeName === 'select'
    },
    isPostedStatus(field) {
      return this.table.schema.postedStatusGuid === field.fieldGuid;
    },
    isInvalidDate(date) {
      return date === 'Invalid date';
    },
    hasPostedValue(item) {
      const postedStatusField = this.localFields.find(field => this.isPostedStatus(field));
      return postedStatusField ? item[postedStatusField.name] === 'Posted' : false;
    },
    canEditPostedStatus() {
      return [
        this.permissionTypes.admin,
        this.permissionTypes.edit,
        this.permissionTypes.unpost,
        this.permissionTypes.post,
      ].includes(this.currentUserPermission);
    },
    isFieldHierarchy(field) {
      const type = field.fieldType || field.dataTypeName;
      return type === 'parentChildHierarchy';
    },
    isFieldHierarchyNotTree(field) {
      const type = field.fieldType || field.dataTypeName;
      const jsonData = typeof field.dataTypeJSON === 'string' ? JSON.parse(field.dataTypeJSON) : field.dataTypeJSON;
      return type === this.fieldValues.parentChildHierarchy && (jsonData.ControlType === 'list' || jsonData.ControlType === 'select');
    },
    isFieldPostedStatus(fieldType) {
      return fieldType === 'postedStatus';
    },
    displayLabel(data) {
      if (data.field.dataTypeName === 'createdDate') {
        return 'Created Date';
      } else if (data.field.dataTypeName === 'lastUpdatedDate') {
        return 'Update Date';
      } else if (data.field.dataTypeName === 'lastUpdatedBy') {
        return 'Created By';
      } else {
        return data.field.displayName || data.label;
      }
    },
    bulkUpdateCheck(checked, item) {
      if (checked) {
        this.setCheckedItems({data: item, type: null, totalRows: this.totalRows});
      } else {
        this.unsetUncheckedItem({
          itemGuid: item[this.primaryKeyName],
          type: null,
          primaryKeyName: this.primaryKeyName,
        });
      }
    },
    checkAll(checked) {
      if (checked) {
        this.setCheckedItems({
          data: this.localItems.filter(item => !this.hasPostedValue(item)),
          type: 'all',
          totalRows: this.totalRows,
        });
      } else {
        this.unsetUncheckedItem({type: 'all'});
      }
    },
    isAdmin() {
      return [this.permissionTypes.admin].includes(this.currentUserPermission);
    },
    getStatusValue(item) {
      return item[this.validation.status] === this.validation.validationFailed
        ? 'Validation Failed'
        : capitalize(item[this.validation.status]);
    },
    getStatusBadgeVariant(item) {
      return item[this.validation.status] === this.validation.validationFailed
        ? 'danger'
        : item[this.validation.status] === this.validation.validated
          ? 'warning'
          : 'success';
    },
    dateTimeFormat(field) {
      return JSON.parse(field.dataTypeJSON).FormatString
        ? `${JSON.parse(field.dataTypeJSON).FormatString} HH:mm:ss`
        : '';
    },
    async bulkEditRows() {
      const params = {
        inline: false,
        isBulkEditModal: true,
        filters: this.pagedDataParams.filters,
        record: {},
      };
      this.showAddNewRecordModal(params);
    },
    isLineBreakTextField(field) {
      const dataTypeJSON = JSON.parse(field.dataTypeJSON);
      return dataTypeJSON.IsLineBreak;
    },
    capitalize(val) {
      return capitalize(val);
    },
    chooseView({view}) {
      this.$emit('chooseView', {view});
    },
    getFilters() {
      const filters = {};
      for (const key in this.filters) {
        const filter = this.filters[key];
        let f = {
          fieldType: filter.fieldType,
          label: filter.label,
        };

        switch (filter.fieldType) {
        case 'lookup':
          f.selectItems = filter.selectItems;
          break;
        case 'date':
        case 'dateTime':
          f.fromDate = filter.fromDate;
          f.toDate = filter.toDate;
          break;
        case 'text':
        case 'wholeNumber':
        case 'decimalNumber':
          f.text = filter.text;
          f.type = filter.type;
          break;
        case 'boolean':
          f.selected = filter.selected;
          break;
        default:
          f = filter;
        }

        filters[key] = f;
      }

      return filters;
    },
    isCalculatedLookup(field) {
      if (field.dataTypeName === this.fieldValues.lookup) {
        let dataTypeJSON;

        if (field.dataTypeJSON) dataTypeJSON = JSON.parse(field.dataTypeJSON);

        return dataTypeJSON.IsCalculated;
      }
    },
    onOutsideClick() {
      this.closeContextMenu();
    },
    async hideShow(entries) {
      const parsedEntries = entries.map(entry => {
        const target = entry.target;
        const column = target.getAttribute('column');

        return {
          isIntersecting: entry.isIntersecting,
          column,
        };
      });
      this.fieldsForTable = this.fieldsForTable.map(field => {
        if (!field || !field.key) return field;
        const entry = parsedEntries.find(entry => entry.column === field.key);

        if (entry) {
          if (('key' in entry)) {
            return {...field, isIntersecting: true};
          }
          return {...field, isIntersecting: entry.isIntersecting};
        }
        return field;
      });
    },
    /* Determine class to give to the field for cell highlighting
      * red for 'validationWarning' and yellow for 'dataQuality'
      * only assign 'dataQuality' class if all validation failures have type 'dataQuality'  
    */
    getValidationType(failedFields, fieldGuid) {
      const matchedFailedFields = failedFields.filter(f => f.fields.includes(fieldGuid));
      if (matchedFailedFields.length > 0 && matchedFailedFields.every(f => f.type === 'dataQuality')) {
        return 'dataQuality';
      } else if (matchedFailedFields.length > 0) {
        return 'validationWarning';
      }
    },
    getWarning(failedFields, fieldGuid) {
      const matchedFailedFields = failedFields.filter(f => f.fields.includes(fieldGuid));
      const warnings = matchedFailedFields.map(f => f.warning);
      return warnings.join(', ');
    },
  },
  async mounted() {
    /**
     * this.isRowsAfterLoadingExist - is a value that is required to determine whether the initial records in the table exist. 
     * It remains unchanged throughout the life cycle of the component. It is used to correctly display the table after filtering.
     */
    this.isRowsAfterLoadingExist = this.totalRows;
    this.intl = new Intl.Collator();
    this.localFields = JSON.parse(JSON.stringify(this.fields));
    this.localItems = [...this.items];
    this.backupFields = JSON.parse(JSON.stringify(this.fields));

    this.mergedFields();
    if (this.$route.query.filters) {
      const queryFilters = JSON.parse(decodeURIComponent(this.$route.query.filters));
      if (queryFilters && Object.keys(queryFilters).length) {
        this.filters = queryFilters;
      }
    }

    const resize = localStorage.getItem('RESIZE');
    if (resize) {
      this.columnWidths = JSON.parse(resize);
    }
    this.currentPage = this.table.pagination.currentPage;

    this.$nextTick(() => {
      if (this.$refs.table) {
        this.observer = new IntersectionObserver(this.hideShow, {
          root: this.$refs.table.$el,
          threshold: 0.1,
        });
        this.allRows = this.$refs.table.$el.querySelectorAll('th');
        this.allRows.forEach(tr => this.observer.observe(tr));
        this.$refs.table.$el.addEventListener('scroll', this.onScrollChange);
      }
    });

    
    const allIds = [];
    
    if(this.localItems && this.tableData.rlsField && this.table.schema.fields){
      var rlsFieldName = this.table.schema.fields.find(x=>x.fieldGuid == this.tableData.rlsField).name;
      this.localItems.forEach(row => {          
        if (row[rlsFieldName]) {
          try {
            const ids = JSON.parse(row[rlsFieldName]);
            allIds.push(...ids);
          } catch (e) {
            console.warn('Invalid JSON for field:', field.name);
          }
        }
      });

      // Deduplicate
      const uniqueIds = [...new Set(allIds)];

      // Fetch users
      if (uniqueIds.length) {
        const result = await this.getUsersAndGroupsForIds(uniqueIds);
        this.allRLSUsers = result.body;
      }
    }
  },
  beforeUnmount() {
    this.$refs.table.$el.removeEventListener('scroll', this.onScrollChange);
    this.allRows.forEach(tr => this.observer.unobserve(tr));
  },
};
</script>

<style lang="scss">
@import "../../scss/variables";

.no-filter-results{
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.action-button {
  cursor: pointer;
}

.horizontal-scroll {
  overflow-x: hidden;
}

.complex-table-component {
  height: 100%;
  overflow: hidden;
}

.complex-table-th {
  color: #fff;
  background-color: #343a40;
  border-color: #454d55;
}

.complex-table {
  position: relative;
  height: 100%;
  overflow: hidden;
  white-space: nowrap;

  .per-page-select {
    width: 100px;
  }

  .button-wrapper {
    flex-direction: row;
    justify-content: flex-end;
    align-items: center;
  }

  @supports (position: sticky) {
    .b-table-sticky-header > .table.b-table > thead > tr > th {
      position: sticky !important;
      z-index: 4;
    }
    .b-table-sticky-header > .table.b-table > thead > tr > th.b-table-sticky-column {
      position: sticky !important;
      z-index: 5;
    }
    .b-table-sticky-header > .table.b-table > tbody > tr > .b-table-sticky-column {
      z-index: 1;
    }
    .table.b-table > tbody > tr > .table-b-table-default.col-selectColumn {
      background-color: white;
    }

    .table.b-table > tbody > tr.group-row,
    .table.b-table > tbody > tr.group-row td,
    .table.b-table > tbody > tr.subgroup-row,
    .table.b-table > tbody > tr.subgroup-row td {
      background-color: #e8e8e8 !important;
      background-image: none !important;
      position: sticky;
    }
  }


  > .table-wrapper {
    margin-bottom: 0;
    flex: 1;
    overflow: hidden;

    .b-table-sticky-header {
      max-height: 100%;
    }

    table {
      border-bottom: 1px solid #ddd;
      table-layout: fixed;
      margin-bottom: 0;

      > thead {
        > tr {
          position: relative;
          z-index: 51;

          > th {
            background: #0F355E;
            border-bottom: none;
            position: sticky;
            z-index: 10;
            top: 0;

            .header-resize-action {
              position: absolute;
              cursor: col-resize;
              right: 0;
              top: 0;
              bottom: 0;
              border-left: 5px solid transparent;
              height: 30px;
            }

            .header-actions {
              position: absolute;
              right: 0;
              top: 0;
              bottom: 0;
              width: $complex-table-header-dropdown-caret-width;
              height: 30px;

              .dropdown-toggle {
                padding: 0;
                border-radius: 0;
                display: flex;
                justify-content: center;
                align-items: center;
                border-left: $table-border-width solid $table-border-color;
                border-right: $table-border-width solid $table-border-color;

                &:hover {
                  background-color: $complex-table-header-dropdown-caret-hover-bg;
                }
              }

              .dropdown-menu {
                min-width: 14rem;
              }

              .dropdown-item,
              .b-dropdown-form {
                outline: none !important;
              }

              .dropdown-header {
                padding-left: 1rem;
                padding-right: 1rem;
              }

              .dropdown-item {
                svg {
                  width: 1em;
                }
              }

              .dropdown-header {
                font-size: 0.75rem;
              }
            }

            &:hover {
              background-color: #326194;
            }

            // Add border to document field header as it has not filter
            &.document-field-header {
              border-right: 1px solid #dee2e6;
            }
          }
        }
      }

      > tbody {
        .table-failed {
          background: #ffe6e6;
        }

        .table-validated {
          background: #ffffe6;
        }

        > tr {
          transition: 0.4s;

          > td {
            max-width: 150px;
            //width: 150px;
            padding: 0;
          }
        }
      }

      .td-content-wrapper {
        width: 100%;
        height: 100%;

        div:focus {
          outline: none;
        }

        .input-editor-wrapper {
          display: none;
          position: relative;

          .input-editor {
            display: block;
            width: 100%;
            margin: 0;
            padding: 4px 7px;
            border-radius: 0;
            line-height: 22px;
            height: auto;
            box-shadow: none;
            border: 1px solid $primary
          }

          .button-toolbar {
            position: absolute;
            top: 100%;
            left: 50%;
            width: 160px;
            margin-top: -2px;
            margin-left: -80px;
            background-color: #FFF;
            padding: 4px 8px;
            border: 1px solid $primary;
            border-top: 2px solid #FFF;
            display: flex;
            justify-content: center;
          }

          &.editor-wrapper-select {
            .input-editor {
              padding: 0;
            }

            button {
              padding: 4px 7px;
              line-height: 20px;
              border-radius: 0;
              width: 100%;
              text-align: left;
            }

            .dropdown-menu {
              width: 100%;
              min-width: 0;
              border-radius: 0;

              > li {
                > a {
                  padding: 4px 7px;
                }
              }
            }
          }
        }

        .td-content {
          padding: 4px 8px;
          line-height: 24px;
          width: 100%;
          min-height: 32px;
          height: 100%;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          border: 1px solid transparent;

          .not-exist {
            text-decoration: red wavy underline;
          }
        }

        &.td-editing {
          .input-editor-wrapper {
            display: block;
          }

          .td-content {
            display: none;
          }

          .lkp__multiselect--wrapper {
            margin-bottom: 0;
          }
        }
      }

      &.table.b-table.table-sm > thead > tr > [aria-sort]:not(.b-table-sort-icon-left),
      &.table.b-table.table-sm > tfoot > tr > [aria-sort]:not(.b-table-sort-icon-left) {
        /*background-position: right calc(0.3rem / 2) center;*/
        /*padding-right: calc(0.3rem + 0.65em);*/
        // Default is icon on the right
        background-position: right calc(#{$table-cell-padding} / 2 + #{$complex-table-header-dropdown-caret-width}) center;
        padding-right: calc(#{$table-cell-padding} + #{$b-table-sort-icon-bg-width} + #{$complex-table-header-dropdown-caret-width});
      }
    }
  }

  .multiselect__single {
    font-size: 13px;
    color: black;
    padding: 0;
  }

  .multiselect__option, .multiselect__option span {
    font-size: 13px;
    color: black;
  }

  .multiselect__input, .multiselect__input::placeholder {
    font-size: 13px;
  }

  .multiselect, .multiselect__tags, .multiselect__select, .multiselect__option, .multiselect__option::after {
    min-height: 32px;
    height: 32px;
  }

  .multiselect__option {
    padding: 8px;
  }

  .multiselect__tags {
    padding: 5px 40px 0 8px;
  }

  .multiselect__select {
    height: 32px;
  }

  .multiselect__option--disabled {
    //opacity: 0.5;
  }

  .vue-treeselect__list {
    .vue-treeselect__list {
      .vue-treeselect__list-item {
        padding-left: 25px !important;
      }
    }
  }

  .disabled {
    opacity: 0.7;
    cursor: not-allowed;
    color: #7b8a8b;
  }

}

.button-wrapper {
  form {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: row;

    fieldset {
      margin: 0;
    }
  }

  button {
    z-index: 2;
  }

  .form-group {
    .invalid-feedback {
      display: none !important;
    }

    .input-label {
      display: none;
    }
  }

  .form-wrapper {
    input {
      transform: translateX(100%);
      opacity: 0;
      transition: opacity .25s, transform .25s;
      z-index: -1;

      &:focus {
        transform: translateX(0);
        opacity: 1;
        z-index: 1;
      }
    }

    button {
      white-space: nowrap;
    }

    &:hover {
      input {
        transform: translateX(0);
        opacity: 1;
        z-index: 1;
      }
    }
  }

  .badge-wrapper {
    width: auto;
    border-radius: 0.5rem;
    border: 1px solid $gray-300;
    padding: 0.3rem;

    .filter-item-badge {
      padding: 0;

      .badge-text {
        display: inline-block;
        padding: 0.3rem 0 0.3rem 0.75rem;
      }

      .delete-filter-item {
        display: inline-block;
        padding: 0.3rem;
        border-top-right-radius: 0.25rem;
        border-bottom-right-radius: 0.25rem;
        cursor: pointer;

        &:hover {
          background-color: rgba(0, 0, 0, 0.2);
        }
      }
    }
  }

  .button-wrapper-inner {
    justify-content: center;
    align-items: center;
  }
}

.main-header-wrapper {
  align-items: baseline;
}

.check-all::after {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  border-right: 1px solid #dee2e6;
}

.disable-text {
  opacity: 0.7;
}

.editor-w {
  width: 100% !important;
}

.disabled-button {
  opacity: 0.5;
  pointer-events: none;
}

.h-93 {
  height: 93% !important;
}

.cell-loading {
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 100;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
}

.group-handler {
  cursor: pointer;
}

.full-min-height {
  min-height: 100%;
}

.with_context_menu {
  filter: none;
  box-shadow: 2px 2px 10px 4px rgba(0, 0, 0, 0.2);
}

.without_context_menu {
  filter: blur(2px);
  box-shadow: none;
}

.grouping-remove {
  color: #0F355E;
  background-color: transparent;
  border: none;
  transition: 0.4s;

  &:hover {
    text-decoration: underline;
  }
}

tr.limited-row-height .td-content-wrapper {

  white-space: nowrap;
  text-overflow: ellipsis;

  & span {
    display: block;
    max-height: 75px;
  }
}

.filters-wrapper,
.filters-content {
  display: flex;
}

.filters-wrapper {
  justify-content: center;
  align-items: start;
  flex-direction: column;
  gap: 10px;
}

div[column="RecordStatus"] span {
  display: block;
  width: fit-content;
  height: fit-content;
  margin: auto;
}


.filters-content {
  justify-content: space-between;
  padding: 10px;
}
.citation-link {
  color: black;
  padding: 0px 2px;
  font-size: 10px;
  border-radius: 50%;
  margin: 0px 2px;
  background-color: lightgray;
  text-decoration: none;
  &:hover{
    text-decoration: none;
  }
}
.rls {
  display: inline-block;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

// Match Bootstrap 'danger' alert colours
.cell-validationWarning {
  background-color: #f5c6cb;
  color: #721c24;
  border: 0.5px solid #f8d7da;
}

// Match Bootstrap 'warning' alert colours
.cell-dataQuality{
  background-color: #fff3cd;
  color: #856404;
  border: 1px solid #ffeeba;
}

.cursor-pointer {
  cursor: pointer;
}
</style>
